import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FIX_FIELD_PARAM, focusFixField } from '../utils/focusFixField';
import {
  readChartFixTarget,
  withoutChartFixTarget,
} from '../components/oasis/cmsFixTarget';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CalendarPlus,
  ChevronDown,
  ClipboardList,
  FileArchive,
  FileText,
  Pencil,
  Plus,
  StickyNote,
  Trash2,
  Target,
} from 'lucide-react';
import { EvvReadingsDialog } from '../components/visit/EvvReadingsDialog';
import VisitTaskPicker from '../components/visit/VisitTaskPicker';
import PatientAllergies from '../components/PatientAllergies';
import { outstandingLines } from '../components/imported/importedChartLines';
import { EventsTab } from '../components/patient/EventsTab';
import { AuthorizationsTab } from '../components/patient/AuthorizationsTab';
import { InsuranceTab } from '../components/patient/InsuranceTab';
import { TasksTab } from '../components/patient/TasksTab';
import { AddTaskDialog } from '../components/patient/AddTaskDialog';
import GoalsTab from '../components/goals/GoalsTab';
import AddExternalEmrGoalsDialog from '../components/goals/AddExternalEmrGoalsDialog';
import ContinueGoalsFromPreviousEpisode from '../components/goals/ContinueGoalsFromPreviousEpisode';
import { PatientWounds } from '../components/wounds';
import { formatWoundVisitActivitySuffix } from '../components/wounds/woundUtils';
import { VitalsTab } from '../components/patient/VitalsTab';
import { ExternallyBilledBadge } from '../components/episode/ExternallyBilledBadge';
import { UnsetBilledExternallyButton } from '../components/episode/UnsetBilledExternallyButton';
import TakeOverDialog from '../components/episode/TakeOverDialog';
import UndoTakeOverDialog from '../components/episode/UndoTakeOverDialog';
import {
  ChartMovedNotice,
  TakeOverUndoneTrace,
} from '../components/episode/ChartMovedNotice';
import { chartMovedEpisode } from '../components/episode/chartMovedAt';
import { UNDO_BUTTON } from '../components/episode/takeOverCopy';
import {
  getPatient,
  updateOrder,
  getPatientWounds,
  getPatientWoundVisitActivity,
  getPatientDocuments,
  getIntakes,
  updateVisit,
  getPatientAllergies,
  getPatientMedications,
  getImportedAllergyProfile,
  getImportedMedicationLines,
  listPatientCommunications,
  updatePatientCommunication,
  updateMiscTask,
  listAgencyClinicPhysicians,
  batchPrintEpisodeTasks,
  listPatientInsurances,
  updateEpisodeAssignments,
} from '../services/api';
import { useStore } from '../store';
import {
  canCarryForwardGoals,
  canEditEpisodeManagerTask as canEditEpisodeManagerTaskPermission,
  getAssignedClinicianUserId,
  getEpisodeEditorCapabilities,
  lockEpisodeCareFields,
  REOPEN_ONLY_CAPABILITIES,
} from '../utils/chartEditPermissions';
import PatientDocumentsTab from '../components/documents/PatientDocumentsTab';
import ScheduleVisitsDialog from '../components/ScheduleVisitsDialog';
import type { PatientDocumentsTabHandle } from '../components/documents/PatientDocumentsTab';
import { PatientMedications } from '../components/medications';
import { countActiveMedications } from '../components/medications/medicationUtils';
import type {
  PatientResponse,
  PatientInsuranceResponse,
  TaskListResponse,
  ClinicPhysicianResponse,
  PatientCommunicationResponse,
  OrderResponse,
  VisitTypeId,
} from '../services/api/types.gen';
import { AUTHORIZATION_ERROR_CODE } from '../utils/authorizationBudget';
import { useVisitTypeOptions } from '../hooks/useVisitTypeOptions';
import { useToast } from '../hooks/useToastContext';
import { usePrintView } from '../hooks/usePrintView';
import { usePdfExport } from '../hooks/usePdfExport';
import { useActiveGoalCount } from '../hooks/useActiveGoalCount';
import { useAuthorizationBudget } from '../hooks/useAuthorizationBudget';
import { usePatientEligibilityProblems } from '../hooks/usePatientEligibilityProblems';
import { useIsMobile } from '../hooks/useIsMobile';
import { useTakeOverAvailable } from '../hooks/useTakeOverAvailable';
import {
  usePatientDetailEpisodes,
  useAddTaskTargetEpisode,
} from '../hooks/usePatientDetailEpisodes';
import { usePatientDetailTasks } from '../hooks/usePatientDetailTasks';
import { usePatientAssignees } from '../hooks/usePatientAssignees';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  formatDateToMMDDYYYY,
  formatDateToISO,
  getBusinessToday,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { useCanAgencyImportFromExternalEmr } from '../hooks/useExternalEmrImport';
import { useExternalEmrImportReadiness } from '../hooks/useExternalEmrImportReadiness';
import {
  addEpisodeUrlDecision,
  externalEmrOf,
  lastEpisodeHasEnded,
} from '../services/externalEmrPatients';
import { ExternalEmrChartPanel } from '../components/patient/externalEmr/ExternalEmrChartPanel';
import {
  ADD_EPISODE_UNAVAILABLE_TITLE,
  SHELL_PAGE_ADD_EPISODE_LINE,
} from '../components/patient/externalEmr/externalEmrPatientCopy';
import {
  getBusinessErrorMessage,
  getConflictCode,
} from '../utils/apiErrorDetail';
import { useCanViewSchedulingPointLoads } from '../hooks/useCanViewSchedulingPointLoads';
import { useInvalidateSchedulingPointLoads } from '../hooks/useSchedulingPointLoads';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { PatientHeader } from '../components/patient/PatientHeader';
import { DuplicateImportNotice } from '../components/patient/DuplicateImportNotice';
import {
  EpisodeManagedInExternalEmrNotice,
  GoalsChartsFromNote,
  ManagedInExternalEmrNotice,
} from '../components/imported/ManagedInExternalEmrNotice';
import {
  IMPORTED_CHART_BEFORE_TAKE_OVER_BANNER,
  MANAGED_IN_EXTERNAL_EMR_TASK_BANNER,
} from '../components/task/externalCustody';
import { PatientInfoTab } from '../components/patient/PatientInfoTab';
import { PatientFaceSheet } from '../components/patient/PatientFaceSheet';
import AdmissionTab from '../components/patient/AdmissionTab';
import { PatientInsuranceFormDialog } from '../components/patient/PatientInsuranceFormDialog';
import { ThirtyDaySummaryDetailPanel } from '../components/patient/ThirtyDaySummaryDetailPanel';
import { THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE } from '../components/patient/patientCommunicationTypes';
import { CommunicationDetailPanel } from '../components/patient/CommunicationDetailPanel';
import { MiscTaskDetailPanel } from '../components/patient/MiscTaskDetailPanel';
import EpisodeEditor from '../components/episode/EpisodeEditor';
import EpisodeDateSyncDialog from '../components/episode/EpisodeDateSyncDialog';
import {
  boundaryRescheduleBounds,
  type EpisodeBoundary,
} from '../utils/episodeBoundaryVisits';
import { OASIS_VISIT_TYPE_LABELS } from '../constants/oasisVisitTypes';
import { getHttpStatus } from '../utils/httpErrorUtils';
import {
  EpisodeInsuranceSelector,
  type EpisodeInsuranceSwitchOptions,
} from '../components/episode/EpisodeInsuranceSelector';
import { EpisodeWorksheetEditor } from '../components/episode/EpisodeWorksheetEditor';
import { VisitDetailPanel } from '../components/visit/VisitDetailPanel';
import { OrderDetailPanel } from '../components/order/OrderDetailPanel';
import AddEpisodeModal from '../components/AddEpisodeModal';
import type {
  ConfirmSpec,
  SelectionAction,
} from '../components/SelectionActionBar';
import BatchNotesDialog from '../components/task/BatchNotesDialog';
import {
  deleteConflictCode,
  deleteConflictCopy,
  deleteConflictSentence,
  deleteTaskRequest,
  partitionDeletable,
  partitionNotStarted,
  partitionPrintable,
  buildPrintConfirm,
  toBatchPrintTaskRefs,
} from '../components/task/batchActions';
import {
  getDisciplineForVisitType,
  showsGoalsSection,
  supportsWounds,
} from '../constants/visitTypes';
import { visitGoalCapabilities } from '../utils/goalTaskCapabilities';
import { isRecertVisitType, recertWindow } from '../utils/recertWindow';
import { PermissionSlug } from '../constants/featureFlags';
import { isVisitTaskRow } from '../components/task/taskTypeFilters';
import { RestoreTaskButton } from '../components/task/RestoreTaskButton';
import { BillingManagerLink } from '../components/billing/BillingManagerLink';
import HhrgWorksheetTab from '../components/billing/HhrgWorksheetTab';
import { UnclaimedClinicianName } from '../components/clinician/UnclaimedClinicianName';
import CareTeamChatButton from '../components/chat/CareTeamChatButton';
import { useSetChatContextEpisode } from '../hooks/chat/useSetChatContextEpisode';
import {
  activeEpisodeBoundaryForVisit,
  episodeBlocksNewVisits,
  episodeClosedPeriodBlocksNewVisits,
} from '../components/patient/patientDetailTaskConstants';
import {
  closedEpisodeCopy,
  useCanAmendClosedEpisode,
} from '../utils/closedEpisodeLock';
import { useBreadcrumbs } from '../components/breadcrumbs/BreadcrumbContext';

const LoadingSpinner: React.FC<{ size?: 'sm' | 'md'; text?: string }> = ({
  size = 'md',
  text,
}) => {
  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-8 w-8';
  return (
    <span className='flex items-center gap-1'>
      <div
        className={`animate-spin rounded-full border-b-2 border-primary ${sizeClass}`}
      />
      {text && <span>{text}</span>}
    </span>
  );
};

const PATIENT_TABS = [
  'visits',
  'documents',
  'medications',
  'allergies',
  'wounds',
  'vitals',
  'goals',
  'insurance',
  'authorizations',
  'events',
  'info',
  'admission',
  'hhrg-worksheet',
] as const;
type PatientTab = (typeof PATIENT_TABS)[number];

function isValidTab(value: string | undefined): value is PatientTab {
  return PATIENT_TABS.includes(value as PatientTab);
}

/** Tab shown on first paint from URL (visit detail route takes precedence). */
function initialTabFromUrl(params: {
  visitId?: string;
  tab?: string;
}): PatientTab {
  if (params.visitId) return 'visits';
  return isValidTab(params.tab) ? params.tab : 'visits';
}

/**
 * Whether a visit task's type has a wound / goals section. `visit_type` is typed
 * as a loose string on the task payload, so narrow it before consulting the
 * capability lists: an unset or unrecognized value means "no section" rather
 * than a crash.
 */
const taskSupportsWounds = (task: TaskListResponse): boolean =>
  !!task.visit_type && supportsWounds(task.visit_type as VisitTypeId);

const taskShowsGoalsSection = (task: TaskListResponse): boolean =>
  !!task.visit_type && showsGoalsSection(task.visit_type as VisitTypeId);

const PageLoading: React.FC<{ message: string }> = ({ message }) => (
  <div className='flex items-center justify-center h-64'>
    <div className='text-center'>
      <div className='flex justify-center mb-4'>
        <LoadingSpinner size='md' />
      </div>
      <p className='text-muted-foreground'>{message}</p>
    </div>
  </div>
);

const PatientDetail: React.FC = () => {
  const {
    id,
    tab: tabParam,
    visitId: visitIdParam,
  } = useParams<{
    id: string;
    tab?: string;
    visitId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // A link from the OASIS Manager's error list opens the chart at the fix.
  const chartFixTarget = useMemo(
    () => readChartFixTarget(searchParams),
    [searchParams]
  );
  const clearChartFixTarget = useCallback(
    () =>
      setSearchParams(previous => withoutChartFixTarget(previous), {
        replace: true,
      }),
    [setSearchParams]
  );
  const episodeIdFromUrl = searchParams.get('episode');
  const { success: showSuccess, error: showError } = useToast();
  const user = useStore(state => state.user);
  const hasPermission = useStore(state => state.hasPermission);
  const mayDeleteImportedCompleted = hasPermission(
    PermissionSlug.TASKS_DELETE_IMPORTED
  );
  const { options: visitTypeOptions } = useVisitTypeOptions();
  const { printTask, printingTaskId } = usePrintView();
  // A second instance for the deleted list. The live one must stay unwidened,
  // so a task deleted in another tab stops printing rather than printing on
  // regardless. Only one of the two lists is on screen at a time, so the two
  // "which row is printing" states never compete.
  const { printTask: printDeletedTask, printingTaskId: printingDeletedTaskId } =
    usePrintView({ includeDeleted: true });
  // Drives the synchronous-tab-open batch "Combined PDF" export.
  const { exportPdf: exportBatchPdf } = usePdfExport();
  const isMobile = useIsMobile();

  const currentPatientIdRef = useRef(id);
  currentPatientIdRef.current = id;
  const previousPatientIdRef = useRef(id);

  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(
    visitIdParam ?? null
  );
  // Active tab for main content area — synced with URL
  const [activeTab, setActiveTabState] = useState<PatientTab>(() =>
    initialTabFromUrl({
      visitId: visitIdParam,
      tab: tabParam,
    })
  );

  // Returns the page to the task list, for a restore performed from a detail
  // view: the panel is now showing a live task in its read-only deleted mode.
  const closeTaskDetail = useCallback(() => {
    setSelectedVisitId(null);
    navigate(`/patients/${id}`);
  }, [id, navigate]);

  const {
    episodes,
    setEpisodes,
    canAddEpisode,
    addEpisodeBlockedMessage,
    takeoverCandidate,
    episodesSettledPatientId,
    setSelectedEpisodeId,
    effectiveSelectedEpisodeId,
    selectedEpisode,
    previousEpisode,
    otherEpisodes,
    lupaPeriods,
    lupaLoading,
    loadEpisodes,
    fetchLupa,
    lupaLastLoadFailedRef,
  } = usePatientDetailEpisodes({
    patientId: id,
    episodeIdFromUrl,
    currentPatientIdRef,
    lupaEnabled: !visitIdParam,
  });

  const {
    tasks,
    setTasks,
    visitTasks,
    visitsLoading,
    visitsError,
    tasksPatientId,
    tasksRefreshVersion,
    lastTasksRefreshWasInitialRef,
    fetchTasks,
    taskView,
    setTaskView,
    deletedTasks,
    deletedTasksLoading,
    deletedTasksError,
    deletedTasksReady,
    deletedTaskById,
    linkedDeletedTask,
    selectedTaskPending,
    selectedTaskLookupFailed,
    retrySelectedTaskLookup,
    restoringTaskId,
    handleRestoreTask,
    tableTaskTypeFilter,
    setTableTaskTypeFilterFromUser,
    tasksForEpisode,
    episodeVisitTasks,
    displayedTasks,
    calendarTasks,
    documentContextTasks,
    documentsEpisodeScope,
    selectedTaskIds,
    setSelectedTaskIds,
    selectedTasks,
    atSelectionCap,
    selectAllState,
    toggleTaskSelected,
    removeTaskFromSelection,
    clearTaskSelection,
    handleToggleSelectAll,
  } = usePatientDetailTasks({
    patientId: id,
    selectedVisitId,
    effectiveSelectedEpisodeId,
    episodesSettledPatientId,
    currentPatientIdRef,
    closeTaskDetail,
    showSuccess,
    showError,
  });

  const [patient, setPatient] = useState<PatientResponse | null>(null);
  // An External EMR shell has no chart until it is imported: the page shows
  // its header and the import panel, nothing else.
  const isExternalEmrShell = externalEmrOf(patient)?.state === 'shell';
  // The Take Over v2 chart lock. EMR-3064 turned it off, so the server sends
  // false and the write controls below stay enabled.
  const chartLocked = patient?.chart_locked === true;

  const { addTaskTargetEpisode, addTaskTargetClinicId } =
    useAddTaskTargetEpisode({
      patientId: id,
      episodeIdFromUrl,
      selectedVisitId,
      episodes,
      selectedEpisode,
      effectiveSelectedEpisodeId,
      setSelectedEpisodeId,
      tasks,
      linkedDeletedTask,
      visitsLoading,
      tasksPatientId,
      currentPatientIdRef,
      // Wait for the patient, and never read it for a shell.
      holdCurrentEpisodeFallback: patient?.id !== id || isExternalEmrShell,
      patientClinicId:
        patient !== null && patient.id === id
          ? (patient.clinic_id ?? null)
          : null,
    });

  // LUPA counts come from /lupa, but the contributing-visit popover reads the
  // live task list, so a task refresh silently refetches LUPA to keep them level.
  // Read through a ref so returning to the chart does not fetch on its own: that
  // return reloads the tasks, which lands back here.
  const onTaskDetailRef = useRef(Boolean(visitIdParam));
  onTaskDetailRef.current = Boolean(visitIdParam);
  useEffect(() => {
    if (tasksRefreshVersion === 0 || onTaskDetailRef.current) {
      return;
    }
    // The first load changed nothing LUPA has not already read.
    if (
      lastTasksRefreshWasInitialRef.current &&
      !lupaLastLoadFailedRef.current
    ) {
      return;
    }
    void fetchLupa({ silent: true });
  }, [
    tasksRefreshVersion,
    fetchLupa,
    lastTasksRefreshWasInitialRef,
    lupaLastLoadFailedRef,
  ]);

  const [patientInsurances, setPatientInsurances] = useState<
    PatientInsuranceResponse[]
  >([]);
  const [insurancesLoading, setInsurancesLoading] = useState(true);
  const [insurancePopupOpen, setInsurancePopupOpen] = useState(false);
  const [editingInsuranceId, setEditingInsuranceId] = useState<string | null>(
    null
  );
  const [insuranceRefreshTrigger, setInsuranceRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // EMR-2272: let the chat inbox pin this episode's care-team thread. Keep
  // the last episode while a tab/visit route remounts this page. ChatInbox
  // checks the channel's patient against the current route, so preserving this
  // value cannot pin another patient's row during the load.
  const chatContextSettled =
    Boolean(error) || (!loading && episodesSettledPatientId === id);
  useSetChatContextEpisode(
    !loading && !error && patient && episodesSettledPatientId === id
      ? selectedEpisode?.id
      : undefined,
    {
      clearWhenMissing: chatContextSettled,
      clearOnUnmount: false,
    }
  );
  const [communications, setCommunications] = useState<
    PatientCommunicationResponse[]
  >([]);
  const [isEpisodeEditorOpen, setIsEpisodeEditorOpen] = useState(false);
  const [isEpisodeInsuranceEditorOpen, setIsEpisodeInsuranceEditorOpen] =
    useState(false);
  const [isEpisodeWorksheetOpen, setIsEpisodeWorksheetOpen] = useState(false);
  const [isFaceSheetOpen, setIsFaceSheetOpen] = useState(false);
  const {
    clinicians,
    assignableClinicians,
    getClinicAssignableClinicians,
    duplicateClinicianNames,
    duplicateAssignableClinicianNames,
    formatDuplicateClinicianOptionLabel,
    getTaskAssigneeLabel,
    getTaskAssigneeClinicians,
  } = usePatientAssignees({
    patientLoaded: !!patient,
    clinicId: patient?.clinic_id,
    showError,
  });
  const [physiciansByClinic, setPhysiciansByClinic] = useState<
    Record<string, ClinicPhysicianResponse[]>
  >({});
  const documentsRef = useRef<PatientDocumentsTabHandle>(null);
  const communicationsFetchedRef = useRef(false);
  const loadedCommunicationTaskLabelsKeyRef = useRef<string | null>(null);
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);
  // Identifies the row whose last edit the server rejected, and how many times.
  // Feeding the counter into that row's date-picker `key` remounts it so it
  // re-reads the server value instead of keeping the draft the server refused;
  // the picker only re-syncs its text when its `date` prop changes, and a
  // rejected save leaves that prop untouched. Scoped to the one row so a
  // background refresh never interrupts an edit in progress elsewhere.
  const [rejectedEdit, setRejectedEdit] = useState<{
    taskId: string;
    nonce: number;
  } | null>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  // The day an Add Task opened from the calendar lands on. Only read when the
  // dialog mounts, which is what makes "reset on open" the dialog's own job.
  const [addTaskInitialDate, setAddTaskInitialDate] = useState<
    Date | undefined
  >(undefined);
  const [isScheduleVisitsOpen, setIsScheduleVisitsOpen] = useState(false);
  const communicationRequestIdRef = useRef(0);

  const [deleteTask, setDeleteTask] = useState<TaskListResponse | null>(null);
  // EMR-325: which visit's EVV readings are open. One dialog at page level
  // rather than one per row, mirroring how Hotbox drives the same component.
  const [evvVisitId, setEvvVisitId] = useState<string | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  // Scheduling Points (EMR-294): what each candidate is already carrying,
  // beside their name at the moment work is assigned.
  const canViewSchedulingPointLoads = useCanViewSchedulingPointLoads();
  const invalidateSchedulingPointLoads = useInvalidateSchedulingPointLoads();
  // `canViewSchedulingPointLoads` is the whole gate. Deliberately *not* ANDed
  // with the patient's own clinic: the clinic that prices a week is the one on
  // the task or episode being scheduled (`task.agency_clinic_id` /
  // `addTaskTargetClinicId`), and those can differ from `patient.clinic_id`.
  // Gating on the patient got it wrong in both directions — it hid computable
  // loads for a clinic-less patient whose episode had a clinic, and it left the
  // badge silently missing in the reverse case while the explanatory line,
  // keyed on the patient, stayed hidden. Each call site checks the clinic it
  // actually prices by; `patientClinicId` now only picks which sentence is true.
  const patientClinicId = patient?.clinic_id ?? null;

  // A boundary-defining visit (SOC or DC/TRN/DTH) sitting on an episode date was
  // moved off it, so we offer to move the matching episode date to match.
  const [episodeDateSync, setEpisodeDateSync] = useState<{
    boundary: EpisodeBoundary;
    episodeId: string;
    visitId: string;
    newDate: string;
    currentBoundaryDate: string;
    episodeStartDate: string | null;
    episodeEndDate: string | null;
    visitLabel?: string;
  } | null>(null);

  const [addEpisodeOpen, setAddEpisodeOpen] = useState(false);

  const communicationTaskIdsKey = useMemo(
    () =>
      tasks
        .filter(task => task.task_type === 'patient_communication')
        .map(task => task.id)
        .sort()
        .join('|'),
    [tasks]
  );

  const setActiveTab = (tab: PatientTab) => {
    setActiveTabState(tab);
    if (!id) return;
    let path: string;
    const selectedNavigationTask = tasks.find(
      task => task.id === selectedVisitId
    );
    if (
      (tab === 'wounds' || tab === 'goals') &&
      selectedNavigationTask &&
      selectedNavigationTask.task_type !== 'visit'
    ) {
      setSelectedVisitId(null);
    }
    if (
      tab === 'visits' &&
      selectedNavigationTask &&
      selectedNavigationTask.task_type !== 'patient_communication'
    ) {
      path = `/patients/${id}/visits/${selectedVisitId}`;
    } else if (tab === 'visits') {
      setSelectedVisitId(null);
      path = `/patients/${id}`;
    } else {
      path = `/patients/${id}/${tab}`;
      if (tab === 'hhrg-worksheet') {
        const worksheetSearchParams = new URLSearchParams(searchParams);
        worksheetSearchParams.set('payment_path', 'NORMAL_NON_LUPA');
        path += `?${worksheetSearchParams.toString()}`;
      }
    }
    navigate(path, { replace: true });
  };

  // Leaving a task for the chart, by the breadcrumb, a Back button or the
  // browser, reloads the list once: the task's own panel may have changed its
  // status. The Back buttons rely on this rather than fetching themselves.
  const previousVisitIdParam = useRef(visitIdParam);
  useEffect(() => {
    if (previousVisitIdParam.current && !visitIdParam) {
      void fetchTasks();
    }
    previousVisitIdParam.current = visitIdParam;
  }, [visitIdParam, fetchTasks]);

  useEffect(() => {
    if (visitIdParam) {
      setSelectedVisitId(visitIdParam);
      setActiveTabState('visits');
      return;
    }
    const resolved = isValidTab(tabParam) ? tabParam : 'visits';
    setActiveTabState(resolved);
    if (resolved === 'visits') {
      setSelectedVisitId(null);
    }
  }, [tabParam, visitIdParam]);

  /**
   * Reload the patient row through the same path the page first loads it by.
   *
   * Hoisted out of the mount effect because a child can write to the patient
   * record — carrying forward an imported "no known allergies" assertion sets
   * `patients.no_known_allergies` — and the controls that read it stay stale
   * until this runs. One fetch, one place, so a refreshed chart and a freshly
   * loaded one cannot disagree.
   *
   * `initial` marks the mount call. Only that call owns the page's loading and
   * error states: a background refresh that fails must not blank a chart the
   * clinician is already reading.
   */
  const refreshPatient = useCallback(
    async (options?: { initial?: boolean }) => {
      if (!id) return;
      const initial = options?.initial ?? false;
      const patientId = id;

      try {
        const patientData = await getPatient({
          path: { patient_id: patientId },
        });
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        if (patientData && patientData.data) {
          setPatient(patientData.data);
        }
      } catch (err) {
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        console.error('Error fetching patient:', err);
        if (initial) {
          setError('Failed to load patient details');
        }
      } finally {
        if (initial && currentPatientIdRef.current === patientId) {
          setLoading(false);
        }
      }
    },
    [id]
  );

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setInsurancesLoading(false);
      return;
    }

    // Clear patient-scoped state before fetching so stale insurances from the
    // previous patient cannot bleed into PatientHeader.
    setPatient(null);
    setPatientInsurances([]);
    setInsurancesLoading(true);
    // The summary sheet is scoped to one patient, so leaving it open across a
    // navigation would render the arriving patient under the departing one's
    // heading.
    setIsFaceSheetOpen(false);
    setLoading(true);
    setError(null);

    const fetchPatientInsurances = async () => {
      const patientId = id;
      try {
        const res = await listPatientInsurances({
          path: { patient_id: patientId },
        });
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        if (res.data) setPatientInsurances(res.data);
      } catch (err) {
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        console.error('Failed to load patient insurances:', err);
      } finally {
        if (currentPatientIdRef.current === patientId) {
          setInsurancesLoading(false);
        }
      }
    };

    void refreshPatient({ initial: true });
    fetchPatientInsurances();
  }, [id, refreshPatient]);

  // Refetch the copy of the insurance list that feeds PatientHeader and
  // EpisodeInsuranceSelector, for changes made outside the add/edit dialog.
  const refreshPatientInsurances = useCallback(async () => {
    if (!id) return;
    const patientId = id;
    try {
      const res = await listPatientInsurances({
        path: { patient_id: patientId },
      });
      if (currentPatientIdRef.current !== patientId) return;
      if (res.data) setPatientInsurances(res.data);
    } catch (err) {
      console.error('Failed to load patient insurances:', err);
    }
  }, [id]);

  useEffect(() => {
    communicationsFetchedRef.current = false;
    loadedCommunicationTaskLabelsKeyRef.current = null;
    setCommunications([]);
  }, [id]);

  useEffect(() => {
    const loadCommunications = async () => {
      if (!id) return;
      const patientId = id;
      const taskRowsReady = tasksPatientId === id;
      const needsCommunicationLabels = communicationTaskIdsKey !== '';
      const communicationTaskLabelsKey =
        taskRowsReady && needsCommunicationLabels
          ? `${communicationTaskIdsKey}:${tasksRefreshVersion}`
          : null;
      const communicationTaskLabelsStale =
        communicationTaskLabelsKey !== null &&
        loadedCommunicationTaskLabelsKeyRef.current !==
          communicationTaskLabelsKey;
      const shouldLoadForTaskLabels =
        communicationTaskLabelsStale && needsCommunicationLabels;

      if (!shouldLoadForTaskLabels) {
        if (taskRowsReady && !needsCommunicationLabels) {
          loadedCommunicationTaskLabelsKeyRef.current = null;
          communicationsFetchedRef.current = false;
          setCommunications([]);
        }
        return;
      }
      const requestId = communicationRequestIdRef.current + 1;
      communicationRequestIdRef.current = requestId;
      const isLatestCommunicationRequest = () =>
        currentPatientIdRef.current === patientId &&
        communicationRequestIdRef.current === requestId;

      try {
        const res = await listPatientCommunications({
          path: { patient_id: patientId },
        });
        if (!isLatestCommunicationRequest()) {
          return;
        }
        setCommunications(res.data ?? []);
        communicationsFetchedRef.current = true;
        loadedCommunicationTaskLabelsKeyRef.current =
          communicationTaskLabelsKey;
      } catch (err) {
        if (!isLatestCommunicationRequest()) {
          return;
        }
        console.error('Error fetching communications:', err);
        if (!communicationsFetchedRef.current) {
          setCommunications([]);
        }
      } finally {
        // no loading state to clear
      }
    };
    void loadCommunications();
  }, [id, tasksPatientId, communicationTaskIdsKey, tasksRefreshVersion]);

  /**
   * Physicians from the new ClinicPhysician model. Cached per-clinic and
   * lazily fetched on first lookup for that clinic.
   */
  useEffect(() => {
    const clinicId = patient?.clinic_id;
    if (!clinicId) return;
    if (physiciansByClinic[clinicId] !== undefined) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await listAgencyClinicPhysicians({
          path: { agency_clinic_id: clinicId },
        });
        if (!cancelled) {
          setPhysiciansByClinic(prev => ({
            ...prev,
            [clinicId]: response?.data ?? [],
          }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching physicians for clinic:', err);
          setPhysiciansByClinic(prev => ({ ...prev, [clinicId]: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patient?.clinic_id, physiciansByClinic]);

  // The Add Task dialog prices its assignees by the episode's clinic, which can
  // differ from the patient's, so warm that roster too.
  useEffect(() => {
    const clinicId = addTaskTargetClinicId;
    if (!clinicId) return;
    if (physiciansByClinic[clinicId] !== undefined) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await listAgencyClinicPhysicians({
          path: { agency_clinic_id: clinicId },
        });
        if (!cancelled) {
          setPhysiciansByClinic(prev => ({
            ...prev,
            [clinicId]: response?.data ?? [],
          }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching physicians for episode clinic:', err);
          setPhysiciansByClinic(prev => ({ ...prev, [clinicId]: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addTaskTargetClinicId, physiciansByClinic]);

  const formatDate = (dateString: string | null | undefined) => {
    return formatDateToMMDDYYYY(dateString) || 'N/A';
  };

  const isOrder = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'order';
    },
    [tasks]
  );

  const isCommunication = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'patient_communication';
    },
    [tasks]
  );

  const isMiscTask = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'misc_task';
    },
    [tasks]
  );

  const handleTaskUpdate = async (
    taskId: string,
    updates: {
      assigned_clinician_id?: string | null;
      date?: string;
      scheduled_date?: string;
    },
    successMessage: string
  ) => {
    setUpdatingTaskId(taskId);

    const applyLocalPatch = (patch: Partial<TaskListResponse>) => {
      setTasks(prev =>
        prev.map(task => (task.id === taskId ? { ...task, ...patch } : task))
      );
    };

    const assigneePatch = (response?: {
      assigned_clinician_user_id?: string | null;
    }): Partial<TaskListResponse> =>
      updates.assigned_clinician_id !== undefined
        ? {
            assigned_clinician_id: updates.assigned_clinician_id,
            // The response is authoritative when it includes the user identity.
            // Clear an omitted identity so clinician lookup controls access
            // while the silent refresh is still in flight.
            assigned_clinician_user_id: response?.assigned_clinician_user_id,
            // Clear the cached name so the label recomputes from the new id.
            assigned_clinician_name: null,
          }
        : {};

    const refreshTasksAndNotify = async (patch: Partial<TaskListResponse>) => {
      // The update already succeeded, so update the row in place, notify, then
      // reconcile with a silent refresh. Local patching keeps the row correct
      // if that background refresh fails, and never reloads the entire visits
      // section.
      applyLocalPatch(patch);
      showSuccess('Task Updated', successMessage);
      // The workload numbers just became wrong. Dropping the cached weeks here
      // rather than waiting out the stale window is the difference between a
      // scheduler watching the number move as they assign and a scheduler
      // concluding the number is decorative.
      void invalidateSchedulingPointLoads();
      // Re-enable the row's controls before the background refresh so the user
      // is not blocked from further edits while it reconciles. fetchTasks
      // guards concurrent requests on its own via taskRequestIdRef.
      setUpdatingTaskId(null);
      await fetchTasks({ silent: true });
    };

    const reportUpdateFailure = async (failure: unknown) => {
      console.error('Error updating task:', failure);
      // A visit date move returns 422 both when the target date falls outside
      // the episode window and when no authorization covers it (EMR-706). The
      // server says which, so prefer its message and keep the episode copy as
      // the fallback for a 422 with no detail. Scoped to date moves so an
      // unrelated 422 on another update does not borrow the episode copy.
      if (
        updates.scheduled_date !== undefined &&
        getHttpStatus(failure) === 422
      ) {
        const serverMessage = getBusinessErrorMessage(failure);
        if (serverMessage) {
          showError('Unable to Move Task', serverMessage);
        } else {
          showError(
            'Date Outside Episode',
            'The selected date is outside the episode window.'
          );
        }
      } else {
        // Prefer the server's 4xx business message — the billing-cycle lock
        // (409) explains exactly why the date is frozen, which the generic
        // copy cannot. getBusinessErrorMessage withholds 5xx/proxy bodies.
        showError(
          'Update Failed',
          getBusinessErrorMessage(failure) ?? 'Failed to update task.'
        );
      }
      // Nothing was persisted, so pull the row back to server truth and discard
      // the rejected draft still sitting in the row's date picker.
      setRejectedEdit(prev => ({
        taskId,
        nonce: (prev?.taskId === taskId ? prev.nonce : 0) + 1,
      }));
      await fetchTasks({ silent: true });
    };

    // The generated client resolves rather than throws on a non-2xx (its
    // ThrowOnError type parameter defaults to false), so a rejected save
    // arrives here as a result with `error` set and no `data`. Route it to the
    // same reporting path as a thrown network error instead of silently
    // treating it as a no-op.
    const settleUpdate = async <T,>(
      result: { data?: T },
      buildPatch: (data: T) => Partial<TaskListResponse>
    ) => {
      if (result.data) {
        await refreshTasksAndNotify(buildPatch(result.data));
        return;
      }
      await reportUpdateFailure(result);
    };

    try {
      if (isCommunication(taskId)) {
        const response = await updatePatientCommunication({
          path: { communication_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            communication_date: updates.date,
          },
        });
        await settleUpdate(response, data => ({
          ...(updates.date !== undefined ? { task_date: updates.date } : {}),
          ...assigneePatch(data),
        }));
      } else if (isMiscTask(taskId)) {
        const response = await updateMiscTask({
          path: { misc_task_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            task_date: updates.date,
          },
        });
        await settleUpdate(response, data => ({
          ...(updates.date !== undefined ? { task_date: updates.date } : {}),
          ...assigneePatch(data),
        }));
      } else if (isOrder(taskId)) {
        const response = await updateOrder({
          path: { order_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            order_date: updates.date,
          },
        });
        await settleUpdate(response, data => ({
          ...(updates.date !== undefined ? { task_date: updates.date } : {}),
          ...assigneePatch(data),
        }));
      } else {
        const response = await updateVisit({
          path: { visit_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            ...(updates.scheduled_date !== undefined
              ? { scheduled_date: updates.scheduled_date }
              : {}),
          },
        });
        // Visit rows show task_date as the service date. When the target moves,
        // take both dates from the update response — visit_date may stay put
        // once charted times are already set.
        await settleUpdate(response, data => ({
          ...(updates.scheduled_date !== undefined
            ? {
                scheduled_date: data.scheduled_date,
                task_date: data.visit_date,
              }
            : {}),
          ...assigneePatch(data),
        }));
      }
    } catch (err) {
      await reportUpdateFailure(err);
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleAssigneeChange = (taskId: string, clinicianId: string) => {
    handleTaskUpdate(
      taskId,
      { assigned_clinician_id: clinicianId },
      'Assignee updated successfully.'
    );
  };

  const handleTargetDateChange = (
    taskId: string,
    newDate: Date | undefined
  ) => {
    if (!newDate) return;
    const formattedDate = formatDateToISO(newDate);
    if (!formattedDate) return;
    const task = tasks.find(t => t.id === taskId);
    if (task && isVisitTaskRow(task)) {
      // If this visit currently sits on the episode boundary it defines
      // (SOC on start, DC/TRN/DTH on end) and is being moved to a different
      // date, offer to move the episode boundary too. The episode must move
      // first because the reschedule endpoint rejects a date outside the current
      // window, so the offer dialog drives both updates in order.
      const boundary = activeEpisodeBoundaryForVisit(task);
      const boundaryDate =
        boundary === 'start'
          ? task.episode_start_date
          : boundary === 'end'
            ? task.episode_end_date
            : null;
      if (boundary && boundaryDate && formattedDate !== boundaryDate) {
        setEpisodeDateSync({
          boundary,
          episodeId: task.episode_id,
          visitId: task.id,
          newDate: formattedDate,
          currentBoundaryDate: boundaryDate,
          episodeStartDate: task.episode_start_date ?? null,
          episodeEndDate: task.episode_end_date ?? null,
          visitLabel: task.visit_type
            ? OASIS_VISIT_TYPE_LABELS[task.visit_type]
            : undefined,
        });
        return;
      }
      handleTaskUpdate(
        taskId,
        { scheduled_date: formattedDate },
        'Target date updated successfully.'
      );
      return;
    }
    handleTaskUpdate(
      taskId,
      { date: formattedDate },
      'Target date updated successfully.'
    );
  };

  /**
   * Selectable range for a task row's target-date picker. Most tasks are confined
   * to the episode window. A visit sitting on the boundary it defines may move the
   * boundary with it, so that side opens up — but only as far as the episode rules
   * allow (60-day cap, end after start, no overlap with a neighbouring episode),
   * so dates the sync endpoint would reject cannot be picked in the first place.
   */
  const taskTargetDateBounds = useCallback(
    (
      task: TaskListResponse
    ): { minDate: Date | undefined; maxDate: Date | undefined } | null => {
      const boundary = activeEpisodeBoundaryForVisit(task);
      if (boundary && task.episode_start_date && task.episode_end_date) {
        const bounds = boundaryRescheduleBounds({
          boundary,
          episodeId: task.episode_id,
          episodeStartDate: task.episode_start_date,
          episodeEndDate: task.episode_end_date,
          patientEpisodes: episodes,
        });
        return {
          minDate: parseISOToLocalDate(bounds.minDate),
          maxDate: parseISOToLocalDate(bounds.maxDate),
        };
      }
      // A Recert may only move within days 56-60 of its episode; an episode
      // ending before day 56 leaves it no day, so its date is not editable.
      if (isRecertVisitType(task.visit_type)) {
        const recert = recertWindow(
          task.episode_start_date,
          task.episode_end_date
        );
        return recert
          ? {
              minDate: parseISOToLocalDate(recert.start),
              maxDate: parseISOToLocalDate(recert.end),
            }
          : null;
      }
      return {
        minDate: parseISOToLocalDate(task.episode_start_date),
        maxDate: parseISOToLocalDate(task.episode_end_date),
      };
    },
    [episodes]
  );

  const handleDeleteTask = async () => {
    if (!deleteTask) return;

    setIsDeletingTask(true);
    try {
      const deleteTaskId = deleteTask.id;
      const response = await deleteTaskRequest(deleteTask);
      if (response.error) {
        console.error('Error deleting task:', response.error);
        // A 409 is the only error carrying a ConflictCode, and it is the one
        // the user can act on — "this is signed" reads very differently from
        // a flat failure. Everything else falls back to the generic sentence
        // (EMR-1312).
        showError(
          'Delete Failed',
          deleteConflictSentence(deleteConflictCode(response))
        );
        return;
      }
      setTasks(prev => prev.filter(t => t.id !== deleteTaskId));
      setDeleteTask(null);
      // Delete skips fetchTasks (optimistic local remove), so bump LUPA
      // explicitly — otherwise the strip/table counts stay at the old number.
      void fetchLupa({ silent: true });
      // A deleted task leaves its clinician's week — the cached load would
      // otherwise keep counting work that no longer exists for up to a minute.
      void invalidateSchedulingPointLoads();
      showSuccess('Task Deleted', 'Task deleted successfully.');
    } catch (err) {
      console.error('Error deleting task:', err);
      showError('Delete Failed', 'Failed to delete task.');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const canEditEpisodeManagerTask = useCallback(
    (task: TaskListResponse) =>
      canEditEpisodeManagerTaskPermission({
        assignedClinicianUserId: getAssignedClinicianUserId(
          task.assigned_clinician_id,
          clinicians,
          task.assigned_clinician_user_id
        ),
        currentUserId: user?.id,
        // Episode Manager permissions follow the patient's current clinic,
        // matching the chart's existing identity/clinic boundary.
        clinicId: patient?.clinic_id,
        clinicPermissions: user?.user_clinics,
        activeClinicIds: user?.active_clinic_ids,
        hasAgencyPermission: hasPermission,
      }),
    [
      clinicians,
      hasPermission,
      patient?.clinic_id,
      user?.id,
      user?.active_clinic_ids,
      // Unused in the body, but keep it: hasPermission is a stable reference
      // that reads grants from the store, so without this the task rows keep
      // stale edit access after the user's grants change.
      user?.permissions,
      user?.user_clinics,
    ]
  );

  const selectedEpisodeAssignedClinicianUserId = selectedEpisode
    ? getAssignedClinicianUserId(
        selectedEpisode.assigned_clinician_id,
        clinicians,
        selectedEpisode.assigned_clinician_user_id
      )
    : undefined;
  const selectedEpisodeEditorCapabilities = selectedEpisode
    ? getEpisodeEditorCapabilities({
        assignedClinicianUserId: selectedEpisodeAssignedClinicianUserId,
        currentUserId: user?.id,
        clinicId: patient?.clinic_id,
        clinicPermissions: user?.user_clinics,
        activeClinicIds: user?.active_clinic_ids,
        hasAgencyPermission: hasPermission,
      })
    : null;
  // A holder of episode:reopen reaches the editor for a completed or reopened
  // episode even without field access; it then shows only Mark incomplete /
  // completed (EMR-2433). The backend gates both actions.
  const canReopenEpisodes = hasPermission(PermissionSlug.EPISODE_REOPEN);
  const selectedEpisodeReopenable = Boolean(
    selectedEpisode &&
    canReopenEpisodes &&
    (selectedEpisode.status === 'completed' ||
      (selectedEpisode.status === 'active' && selectedEpisode.reopened_at))
  );
  const canEditSelectedEpisode =
    selectedEpisodeEditorCapabilities !== null || selectedEpisodeReopenable;
  // The worksheet owner shortcut requires active access to the patient's
  // clinic. This list comes from live UserClinic memberships.
  const hasCurrentPatientClinicMembership = Boolean(
    patient?.clinic_id && user?.active_clinic_ids?.includes(patient.clinic_id)
  );
  const canClaimSelectedEpisodeWorksheet = Boolean(
    selectedEpisode && hasCurrentPatientClinicMembership
  );

  const getClinicPhysicians = useMemo(() => {
    return (clinicId: string | null | undefined): ClinicPhysicianResponse[] => {
      if (!clinicId) return [];
      return physiciansByClinic[clinicId] ?? [];
    };
  }, [physiciansByClinic]);

  const communicationById = useMemo(
    () =>
      new Map(
        communications.map(communication => [communication.id, communication])
      ),
    [communications]
  );

  // The fetch, its request-id guard and its first-fetch-only placeholder live
  // in the hook so they can be tested without standing up this page.
  const {
    budget: episodeAuthBudget,
    requiresAuthorization: episodeRequiresAuthorization,
    errors: authorizationErrors,
    errorCount: authorizationErrorCount,
    unavailable: authBudgetUnavailable,
    loading: authBudgetLoading,
    refresh: fetchAuthErrors,
  } = useAuthorizationBudget(effectiveSelectedEpisodeId);

  // The problems endpoint needs eligibility:check; skip the fetch for everyone
  // else so chart load does not produce a silent 403.
  const canCheckEligibility = useStore(state =>
    state.hasPermission(PermissionSlug.ELIGIBILITY_CHECK)
  );
  const {
    errors: eligibilityProblems,
    errorCount: eligibilityProblemCount,
    refresh: fetchEligibilityProblems,
  } = usePatientEligibilityProblems(
    canCheckEligibility ? patient?.id : undefined
  );

  const handleUpdateEpisodeInsurance = useCallback(
    async (
      insuranceId: string,
      options: EpisodeInsuranceSwitchOptions = {}
    ) => {
      if (!effectiveSelectedEpisodeId) return;
      const voidBillingCycleIds = options.voidBillingCycleIds ?? [];
      const closeOutEndDate =
        voidBillingCycleIds.length === 0 ? options.closeOutEndDate : undefined;

      try {
        // The client resolves 4xx/5xx as `{ error }`; throw the whole result
        // so the catch sees the status and the dialog stays open.
        const response = await updateEpisodeAssignments({
          path: { episode_id: effectiveSelectedEpisodeId },
          body: {
            patient_insurance_id: insuranceId,
            // At most one billing action rides along (EMR-2361): voided
            // cycles for an error correction, or the close-out end for an
            // insurance change. A plain switch keeps its request shape.
            ...(voidBillingCycleIds.length > 0
              ? { void_billing_cycle_ids: voidBillingCycleIds }
              : {}),
            ...(closeOutEndDate
              ? { close_out_old_insurance: { end_date: closeOutEndDate } }
              : {}),
          },
        });
        if (response.error) throw response;

        // Reload episodes to get updated insurance info
        await loadEpisodes();
        // The response carries no replacement ids, so the toast cannot promise
        // a rebuilt claim (a period not yet due waits for the nightly run).
        showSuccess(
          voidBillingCycleIds.length > 0
            ? `Episode insurance updated; ${voidBillingCycleIds.length} ${
                voidBillingCycleIds.length === 1 ? 'claim' : 'claims'
              } voided; ${
                voidBillingCycleIds.length === 1
                  ? 'its period is'
                  : 'their periods are'
              } rebuilt once due`
            : closeOutEndDate
              ? `Episode insurance updated; claim created under the previous insurance through ${formatDateToMMDDYYYY(closeOutEndDate)}`
              : 'Episode insurance updated successfully'
        );
      } catch (error) {
        console.error('Failed to update episode insurance:', error);
        const errorMessage =
          getBusinessErrorMessage(error) ??
          'Failed to update episode insurance';
        showError(errorMessage);
        throw error;
      }
    },
    [effectiveSelectedEpisodeId, loadEpisodes, showSuccess, showError]
  );

  // Physicians for the summary sheet. The episode's clinic can differ from the
  // patient's, and the episode's physician sits on that clinic's roster, so
  // prefer it and keep the patient's clinic as the fallback. Both rosters are
  // already cached per clinic, so this costs no request. The clinic comes off a
  // task on the episode, which is where the add-task flow reads it from too.
  const faceSheetPhysicians = useMemo(() => {
    const episodeClinicId = selectedEpisode
      ? tasks.find(task => task.episode_id === selectedEpisode.id)
          ?.agency_clinic_id
      : undefined;
    return getClinicPhysicians(episodeClinicId ?? patient?.clinic_id);
  }, [selectedEpisode, tasks, getClinicPhysicians, patient?.clinic_id]);

  const canAmendClosedEpisode = useCanAmendClosedEpisode();
  const targetEpisodeBlocksVisits = episodeBlocksNewVisits(
    addTaskTargetEpisode,
    canAmendClosedEpisode
  );
  const targetEpisodeClosedLocked = episodeClosedPeriodBlocksNewVisits(
    addTaskTargetEpisode,
    canAmendClosedEpisode
  );

  // Unmounting the dialog never fires onOpenChange, so without this the open
  // flag would survive and pop the dialog back up on the next episode that
  // does accept visits.
  useEffect(() => {
    if (!addTaskTargetEpisode || targetEpisodeBlocksVisits) {
      setIsScheduleVisitsOpen(false);
    }
  }, [addTaskTargetEpisode, targetEpisodeBlocksVisits]);

  const openAddTaskDialog = useCallback(
    (initialDate?: Date) => {
      if (chartLocked) return;
      setAddTaskInitialDate(initialDate);
      setIsAddDialogOpen(true);
    },
    [chartLocked]
  );

  useEffect(() => {
    if (previousPatientIdRef.current === id) {
      return;
    }

    previousPatientIdRef.current = id;
    setDeleteTask(null);
    setIsAddDialogOpen(false);
  }, [id]);

  useEffect(() => {
    if (!deleteTask) {
      return;
    }
    if (!tasks.some(task => task.id === deleteTask.id)) {
      setDeleteTask(null);
    }
  }, [deleteTask, tasks]);

  const episodeStatusBadgeVariant = (
    status: string | null | undefined
  ): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'active':
        return 'default';
      case 'completed':
        return 'secondary';
      case 'cancelled':
      case 'interrupted':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Display-only relabel: a cancelled episode reads as "Inactive" (EMR-158). The
  // stored value and badge variant mapping are unchanged.
  const episodeStatusDisplayLabel = (
    status: string | null | undefined
  ): string => (status === 'cancelled' ? 'Inactive' : (status ?? ''));

  const getTaskDisplayName = useCallback(
    (task: TaskListResponse) => {
      if (task.task_type !== 'patient_communication') {
        return task.display_name;
      }

      return (
        communicationById.get(task.id)?.communication_type ??
        'Patient Communication'
      );
    },
    [communicationById]
  );

  const isThirtyDaySummaryTask = useCallback(
    (taskId: string) =>
      communicationById.get(taskId)?.communication_type ===
      THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE,
    [communicationById]
  );

  // ===== Batch actions for the episode task table (ROG-2506) =====
  // The selection is snapshotted when the notes dialog opens rather than read
  // live: the refresh that follows a successful append replaces every task
  // object, which would otherwise change the dialog's own count out from under
  // the toast it triggers.
  const [batchNotesTasks, setBatchNotesTasks] = useState<TaskListResponse[]>(
    []
  );
  const [batchNotesOpen, setBatchNotesOpen] = useState(false);

  // Opens the dialog with the whole selection, not just the eligible rows, so
  // it can disclose what it is about to skip.
  const runBatchAddNote = useCallback((items: TaskListResponse[]) => {
    setBatchNotesTasks(items);
    setBatchNotesOpen(true);
  }, []);

  const runBatchDelete = useCallback(
    async (items: TaskListResponse[]) => {
      const { deletable } = partitionDeletable(
        items,
        mayDeleteImportedCompleted
      );
      if (deletable.length === 0) return;
      const results = await Promise.allSettled(
        deletable.map(task => deleteTaskRequest(task))
      );
      const succeededIds: string[] = [];
      const failures: { task: TaskListResponse; code?: string }[] = [];
      results.forEach((result, index) => {
        const task = deletable[index];
        if (result.status === 'fulfilled' && !result.value.error) {
          succeededIds.push(task.id);
        } else {
          const code =
            result.status === 'fulfilled'
              ? getConflictCode(result.value.error)
              : undefined;
          failures.push({ task, code });
        }
      });
      if (succeededIds.length > 0) {
        const succeeded = new Set(succeededIds);
        setTasks(prev => prev.filter(t => !succeeded.has(t.id)));
        setSelectedTaskIds(prev => {
          const next = new Set(prev);
          succeededIds.forEach(taskId => next.delete(taskId));
          return next;
        });
        // Batch delete skips fetchTasks (optimistic local remove), so refresh
        // LUPA explicitly — including partial successes — or the gauge/strip
        // keep counting visits the popover already dropped.
        void fetchLupa({ silent: true });
      }
      if (failures.length === 0) {
        showSuccess(
          'Tasks deleted',
          `${succeededIds.length} task${succeededIds.length === 1 ? '' : 's'} deleted.`
        );
        return;
      }
      const lines = failures
        .slice(0, 3)
        .map(
          f => `${getTaskDisplayName(f.task)}: ${deleteConflictCopy(f.code)}`
        );
      const more =
        failures.length > 3 ? ` …and ${failures.length - 3} more.` : '';
      showError(
        `Couldn't delete ${failures.length} of ${deletable.length}`,
        lines.join('; ') + more
      );
    },
    [
      fetchLupa,
      getTaskDisplayName,
      mayDeleteImportedCompleted,
      setSelectedTaskIds,
      setTasks,
      showSuccess,
      showError,
    ]
  );

  const runBatchPrint = useCallback(
    (items: TaskListResponse[], format: 'pdf' | 'zip', choiceKey?: string) => {
      const chosen =
        choiceKey === 'completed' ? partitionNotStarted(items).started : items;
      // Drop task types the endpoint cannot render before the length guard:
      // filtering afterwards would send an empty task list (rejected as a 422)
      // and, for the PDF branch, leave an already-opened blank tab behind.
      const { printable: printSet, skippedUnprintable } =
        partitionPrintable(chosen);
      const episodeId = effectiveSelectedEpisodeId;
      if (!episodeId) return;
      if (printSet.length === 0) {
        showError(
          'Nothing to print',
          'The selected tasks have no printable document.'
        );
        return;
      }
      // Both branches call the endpoint identically except for `format`; only
      // the delivery differs (open in a tab vs. download).
      const callBatchPrint = () =>
        batchPrintEpisodeTasks({
          path: { episode_id: episodeId },
          body: { tasks: toBatchPrintTaskRefs(printSet), format },
        });
      if (format === 'pdf') {
        // exportBatchPdf opens the tab synchronously (within this user gesture),
        // then awaits the fetch and fills it from the returned blob.
        return exportBatchPdf(
          'batch-chart-record',
          async () => {
            const result = await callBatchPrint();
            return result.data
              ? {
                  blob: new Blob([result.data as BlobPart], {
                    type: 'application/pdf',
                  }),
                }
              : null;
          },
          'chart record'
        );
      }
      return (async () => {
        try {
          const result = await callBatchPrint();
          if (result.error || !result.data) {
            console.error('Batch chart-record ZIP failed:', result.error);
            showError(
              'Export failed',
              'Could not generate the chart record. Please try again.'
            );
            return;
          }
          // The client-axios result IS the AxiosResponse (with `error`
          // augmented), so it carries the Content-Disposition filename.
          downloadResponseAsFile(result, 'chart_record.zip');
          const skippedNote =
            skippedUnprintable.length > 0
              ? ` ${skippedUnprintable.length} task${
                  skippedUnprintable.length === 1 ? '' : 's'
                } skipped (no printable document).`
              : '';
          showSuccess(
            'Export complete',
            `${printSet.length} task${printSet.length === 1 ? '' : 's'} exported.${skippedNote}`
          );
        } catch (err) {
          console.error('Batch chart-record ZIP error:', err);
          showError(
            'Export failed',
            'Could not generate the chart record. Please try again.'
          );
        }
      })();
    },
    [effectiveSelectedEpisodeId, exportBatchPdf, showError, showSuccess]
  );

  // Both print actions share this gate: if any selected task is not-started it
  // would render as a blank template, so disclose and let the user choose.
  const notStartedConfirm = useCallback(
    (items: TaskListResponse[]): ConfirmSpec | null => buildPrintConfirm(items),
    []
  );

  const allBatchActions = useMemo<SelectionAction<TaskListResponse>[]>(
    () => [
      {
        key: 'print-pdf',
        label: 'Print → Combined PDF',
        icon: FileText,
        confirm: notStartedConfirm,
        run: (items, choiceKey) => runBatchPrint(items, 'pdf', choiceKey),
      },
      {
        key: 'print-zip',
        label: 'Print → Download ZIP',
        icon: FileArchive,
        confirm: notStartedConfirm,
        run: (items, choiceKey) => runBatchPrint(items, 'zip', choiceKey),
      },
      {
        key: 'add-note',
        label: 'Add Note',
        icon: StickyNote,
        run: items => runBatchAddNote(items),
      },
      {
        key: 'delete',
        label: items =>
          `Delete (${partitionDeletable(items, mayDeleteImportedCompleted).deletable.length} eligible)`,
        icon: Trash2,
        iconClassName: 'text-destructive',
        destructive: true,
        confirm: items => {
          const { deletable, skippedLocked } = partitionDeletable(
            items,
            mayDeleteImportedCompleted
          );
          const skipped = skippedLocked.length;
          if (deletable.length === 0) {
            return {
              title: 'Nothing to delete',
              body: 'None of the selected tasks can be deleted \u2014 they are signed, completed, or locked.',
              choices: [{ key: 'ok', label: 'OK' }],
              cancelKeys: ['ok'],
            };
          }
          return {
            title: 'Delete tasks',
            body: `${deletable.length} task${
              deletable.length === 1 ? '' : 's'
            } will be deleted.${
              skipped > 0
                ? ` ${skipped} selected task${
                    skipped === 1 ? '' : 's'
                  } (signed, completed, or locked) will be skipped.`
                : ''
            }`,
            choices: [
              { key: 'cancel', label: 'Cancel', variant: 'outline' },
              { key: 'delete', label: 'Delete', variant: 'destructive' },
            ],
          };
        },
        run: items => runBatchDelete(items),
      },
    ],
    [notStartedConfirm, runBatchPrint, runBatchDelete, runBatchAddNote]
  );
  // Printing reads; a note or a delete writes, and a locked chart takes no
  // writes from Roger.
  const batchActions = useMemo(
    () =>
      chartLocked
        ? allBatchActions.filter(action => action.key.startsWith('print-'))
        : allBatchActions,
    [allBatchActions, chartLocked]
  );

  // A deleted row opens the same detail route a live one does, so the selected
  // task is resolved from whichever list holds it. The live list is consulted
  // first: if a task is in both, it was restored, and the editable view is the
  // truthful one.
  const selectedDeletedTask = useMemo(() => {
    if (!selectedVisitId) return null;
    if (tasks.some(t => t.id === selectedVisitId)) return null;
    return (
      deletedTasks.find(t => t.id === selectedVisitId) ??
      (linkedDeletedTask?.id === selectedVisitId ? linkedDeletedTask : null)
    );
  }, [tasks, deletedTasks, linkedDeletedTask, selectedVisitId]);
  const selectedTask = useMemo(
    () => tasks.find(t => t.id === selectedVisitId) ?? selectedDeletedTask,
    [tasks, selectedVisitId, selectedDeletedTask]
  );
  // Drives read-only mode on every detail panel below.
  const selectedTaskIsDeleted = selectedDeletedTask !== null;
  // A visit URL holds the chart's own loads back, so one that resolves to no
  // task goes to the chart's URL rather than a chart missing them.
  const visitUrlResolvedToNothing =
    Boolean(visitIdParam) &&
    selectedVisitId === visitIdParam &&
    !selectedTaskPending &&
    !selectedTaskLookupFailed &&
    !selectedTask;
  useEffect(() => {
    if (visitUrlResolvedToNothing) {
      navigate(`/patients/${id}`, { replace: true });
    }
  }, [visitUrlResolvedToNothing, id, navigate]);
  // Resolved against the episode's visits, so a selection made under another
  // episode stops being visit context the moment the header switches away from
  // it. `selectedVisitId` itself is left alone: switching back restores the
  // selection, and the Tasks tab's own detail panel reads it from `tasks`.
  const selectedVisitTask = useMemo(
    () => episodeVisitTasks.find(t => t.id === selectedVisitId) ?? null,
    [selectedVisitId, episodeVisitTasks]
  );
  // Wounds and Goals each filter the shared visit selection to types that have
  // that section (ST/transfer/death/supervisory/MSW/aide visits have no wound
  // section; transfer and death visits have no goals section). A selection
  // carried over from the other tab is ignored when it is not eligible here.
  // Goals still need a visit before the list can open. Wounds are patient-scoped:
  // the list stays readable and the in-card picker only enables writes.
  const woundVisitTasks = useMemo(
    () => episodeVisitTasks.filter(task => taskSupportsWounds(task)),
    [episodeVisitTasks]
  );
  const goalVisitTasks = useMemo(
    () => episodeVisitTasks.filter(task => taskShowsGoalsSection(task)),
    [episodeVisitTasks]
  );
  const woundVisitTask =
    selectedVisitTask && taskSupportsWounds(selectedVisitTask)
      ? selectedVisitTask
      : null;
  const goalVisitTask =
    selectedVisitTask && taskShowsGoalsSection(selectedVisitTask)
      ? selectedVisitTask
      : null;
  // Kinnser-migration flow: a migrated episode may have no visit that can open
  // goals, so Kinnser-enabled agencies get a button that saves goals straight
  // onto the selected episode (no visit). The backend enforces the same gate.
  const userAgency = useStore(state => state.user?.agency);
  const canEditPatientInfo = useStore(state =>
    state.hasPermission(PermissionSlug.PATIENT_INFO_EDIT)
  );
  const canViewBilling = useStore(state =>
    state.hasPermission(PermissionSlug.BILLING_ACCESS)
  );

  // The worksheet is conditional on billing access and a loaded episode. A
  // direct deep link can arrive before either condition is known, so wait for
  // episode loading to settle before deciding that the worksheet is unavailable.
  useEffect(() => {
    if (tabParam !== 'hhrg-worksheet') return;
    const noEpisode =
      episodesSettledPatientId === id && selectedEpisode === null;
    if ((!canViewBilling || noEpisode) && id) {
      setActiveTabState('visits');
      setSelectedVisitId(null);
      navigate(`/patients/${id}`, { replace: true });
    }
  }, [
    canViewBilling,
    episodesSettledPatientId,
    id,
    navigate,
    selectedEpisode,
    tabParam,
  ]);

  // EMR-325. Viewing is the gate for the column; removing is a separate slug
  // the dialog checks for itself. Capture is deliberately not offered here —
  // this surface is read-only, unlike Hotbox.
  const showEvvColumn = useStore(state =>
    state.hasPermission(PermissionSlug.EVV_VIEW)
  );
  const canRemoveEvv = useStore(state =>
    state.hasPermission(PermissionSlug.EVV_REMOVE)
  );
  // Ignore a Patient Info save echo for a patient we have already navigated
  // away from. The tab unmounts while `loading` is true, but its in-flight
  // PUT can still resolve and call this after `getPatient(B)` has won.
  const handlePatientUpdated = useCallback((updated: PatientResponse) => {
    if (updated.id !== currentPatientIdRef.current) {
      return;
    }
    setPatient(updated);
  }, []);
  const canAddKinnserGoals = !!userAgency?.is_kinnser_enabled;
  // EMR-2754: offer the previous episode's open goals on a new episode. The
  // slug is clinic-scoped, so it must be held at this patient's clinic.
  const canContinueGoals = canCarryForwardGoals({
    clinicId: patient?.clinic_id,
    clinicPermissions: user?.user_clinics,
    hasAgencyPermission: hasPermission,
  });
  const [isKinnserGoalsOpen, setIsKinnserGoalsOpen] = useState(false);
  // Bumped when the dialog saves a goal so the inline GoalsTab refetches.
  const [kinnserGoalsVersion, setKinnserGoalsVersion] = useState(0);
  // A 30-Day Summary communication renders full-page via its dedicated panel;
  // all other patient_communication types render full-page via
  // CommunicationDetailPanel (see the detail-panel switch in the return).
  const selectedTaskIsThirtyDaySummary = useMemo(
    () => !!selectedVisitId && isThirtyDaySummaryTask(selectedVisitId),
    [selectedVisitId, isThirtyDaySummaryTask]
  );
  const canRenderSelectedTaskDetail =
    activeTab === 'visits' && selectedVisitId && id && selectedTask;

  // Patient Manager › patient, and › the visit when one is open: an open visit
  // is its own page, so the patient's name becomes the way back to the chart.
  const openTaskDate = selectedTask
    ? formatDateToMMDDYYYY(
        selectedTask.task_date ?? selectedTask.scheduled_date
      )
    : '';
  useBreadcrumbs(
    patient
      ? [
          {
            label: `${patient.first_name} ${patient.last_name}`,
            ...(canRenderSelectedTaskDetail ? { to: `/patients/${id}` } : {}),
          },
          ...(canRenderSelectedTaskDetail && selectedTask
            ? [
                {
                  label: [getTaskDisplayName(selectedTask), openTaskDate]
                    .filter(Boolean)
                    .join(' · '),
                },
              ]
            : []),
        ]
      : []
  );

  const handleTaskOpen = useCallback(
    (task: TaskListResponse) => {
      if (!id) return;
      setSelectedVisitId(task.id);
      setActiveTabState('visits');
      navigate(`/patients/${id}/visits/${task.id}`);
    },
    [id, navigate]
  );

  const handleVisitUpdated = useCallback(async () => {
    await Promise.all([loadEpisodes(), fetchTasks()]);
  }, [fetchTasks, loadEpisodes]);

  // A recent-first chart's earlier episodes landed (or stopped trying): the
  // episode list and tasks on screen predate them.
  const handleExternalEmrHistorySettled = useCallback(() => {
    void handleVisitUpdated();
  }, [handleVisitUpdated]);

  // External EMR. A shell is a patient known from the census, with no chart
  // until it is imported; Add Episode waits behind the import. An imported
  // chart whose last episode has ended offers the recert episode next.
  const externalEmr = externalEmrOf(patient);
  const agencyCanImportFromExternalEmr = useCanAgencyImportFromExternalEmr();
  const canStartExternalEmrImport =
    agencyCanImportFromExternalEmr &&
    hasPermission(PermissionSlug.CHART_IMPORT_START);
  // Take Over v2 (an External EMR and the external-emr-take-over flag) needs
  // its own permission, as the server does, and stays offered after syncing
  // stops. Without v2 the basic Take Over from Add Episode needs the import
  // permission, as on main. One path per agency at a time.
  const takeOverV2 = useTakeOverAvailable();
  const canTakeOverExternalEmrPeriod = hasPermission(
    takeOverV2
      ? PermissionSlug.EPISODE_TAKE_OVER
      : PermissionSlug.CHART_IMPORT_START
  );
  const canUnsetBilledExternallyMigration = hasPermission(
    PermissionSlug.ADMIN_SUPER_USER
  );
  // Read once per page view, so the reason shows before anyone presses Import
  // on a shell or Refresh on an imported chart.
  const {
    blockedReason: externalEmrImportBlockedReason,
    loaded: externalEmrImportReadinessLoaded,
  } = useExternalEmrImportReadiness({
    enabled: canStartExternalEmrImport && externalEmr !== null,
  });
  const latestEpisodeEnd = useMemo(
    () =>
      episodes.reduce<string | null>(
        (latest, episode) =>
          episode.end_date && (!latest || episode.end_date > latest)
            ? episode.end_date
            : latest,
        null
      ),
    [episodes]
  );
  const externalEmrRecertDue =
    externalEmr?.state === 'imported' &&
    canAddEpisode &&
    lastEpisodeHasEnded(latestEpisodeEnd, getBusinessToday());
  // A takeover candidate implies Add Episode is open (the server offers one
  // only on an open admission).
  const externalEmrTakeoverDue =
    externalEmr?.state === 'imported' &&
    takeoverCandidate !== null &&
    canTakeOverExternalEmrPeriod;
  // An imported chart offers Take Over on its External EMR bar. Otherwise
  // the episode header offers it, only on the period the server offers.
  const takeOverCandidateOffered =
    takeOverV2 && canTakeOverExternalEmrPeriod && takeoverCandidate !== null;
  const takeOverOnImportBar =
    takeOverCandidateOffered && externalEmr?.state === 'imported';
  const canTakeOverSelectedEpisode =
    takeOverCandidateOffered &&
    !takeOverOnImportBar &&
    selectedEpisode?.id === takeoverCandidate?.episode_id;
  const [takeOverOpen, setTakeOverOpen] = useState(false);
  // PatientDetail stays mounted across patients: a dialog left open must not
  // reopen itself (and queue a refresh) for the next patient's candidate.
  const takeoverEpisodeId = takeoverCandidate?.episode_id ?? null;
  useEffect(() => {
    setTakeOverOpen(false);
  }, [id, takeoverEpisodeId]);
  const movedEpisode = useMemo(() => chartMovedEpisode(episodes), [episodes]);
  // Every kind of move shows the notice (Daniel, 2026-10-13: "3) OK."): the
  // server sends the instant while the moved chart's admission is open.
  const chartMovedMoment = patient?.chart_moved_at ?? null;
  const chartMovedVia = patient?.chart_moved_via ?? null;
  // EMR-3064 / EMR-2889: warn while the External EMR still charts it, never
  // block. The server says when (imported, admission open, not yet moved).
  const showImportedBeforeTakeOverWarn =
    patient?.chart_before_take_over === true;
  // The chart's pre-Take Over banner already warns for every task on it.
  const selectedTaskManagedExternally = useMemo(() => {
    if (showImportedBeforeTakeOverWarn) return false;
    const task = tasks.find(t => t.id === selectedVisitId);
    return task?.managed_in_external_emr === true && !task.chart_locked;
  }, [tasks, selectedVisitId, showImportedBeforeTakeOverWarn]);
  const managedTaskBanner = (
    <ManagedInExternalEmrNotice
      show={selectedTaskManagedExternally}
      message={MANAGED_IN_EXTERNAL_EMR_TASK_BANNER}
    />
  );
  // Undo Take Over: the same gate as Take Over v2, on the period whose Take
  // Over moved the chart, and only when the server says it can be undone (a
  // v1 Take Over cannot; the server re-checks all of it).
  const undoEpisodeId =
    takeOverV2 &&
    canTakeOverExternalEmrPeriod &&
    movedEpisode?.take_over_undoable
      ? movedEpisode.id
      : null;
  const canUndoSelectedEpisode =
    undoEpisodeId !== null && selectedEpisode?.id === undoEpisodeId;
  // The period Undo was opened for, captured at the click. An episodes
  // reload that lands mid-undo must not unmount the dialog and drop its
  // success path, so this does not follow undoEpisodeId.
  const [undoTakeOverEpisodeId, setUndoTakeOverEpisodeId] = useState<
    string | null
  >(null);
  useEffect(() => {
    setUndoTakeOverEpisodeId(null);
  }, [id]);
  const openUndoTakeOver = () => setUndoTakeOverEpisodeId(undoEpisodeId);
  const handleExternalEmrChanged = useCallback(() => {
    void refreshPatient();
    void handleVisitUpdated();
  }, [handleVisitUpdated, refreshPatient]);

  // "Add Episode" on a Patient Manager card links here with
  // ?addEpisode=1: open the dialog once the episodes allow it, then drop the
  // flag so a reload does not reopen it.
  const addEpisodeRequested = searchParams.get('addEpisode') === '1';
  // Wait for this patient and its episodes, so the answer is about them. A
  // shell has no admission to add to, and a blocked chart says why rather than
  // dropping the request silently.
  const patientLoadedForUrl = patient?.id === id;
  useEffect(() => {
    if (!addEpisodeRequested) return;
    const decision = addEpisodeUrlDecision({
      patientLoaded: patientLoadedForUrl,
      episodesSettled: episodesSettledPatientId === id,
      isShell: isExternalEmrShell,
      canAddEpisode,
    });
    if (decision === 'wait') return;
    if (decision === 'open') {
      setAddEpisodeOpen(true);
    } else {
      showError(
        ADD_EPISODE_UNAVAILABLE_TITLE,
        decision === 'refuse_shell'
          ? SHELL_PAGE_ADD_EPISODE_LINE
          : (addEpisodeBlockedMessage ?? undefined)
      );
    }
    const next = new URLSearchParams(searchParams);
    next.delete('addEpisode');
    setSearchParams(next, { replace: true });
  }, [
    addEpisodeBlockedMessage,
    addEpisodeRequested,
    canAddEpisode,
    episodesSettledPatientId,
    id,
    isExternalEmrShell,
    patientLoadedForUrl,
    searchParams,
    setSearchParams,
    showError,
  ]);

  const handleMedicationOrderCreated = useCallback(
    async (order: OrderResponse) => {
      if (!id) return;
      setSelectedVisitId(order.id);
      setActiveTabState('visits');
      navigate(`/patients/${id}/visits/${order.id}`);
      showSuccess(
        'Medication Order Created',
        'Medication order created successfully.'
      );
      try {
        await fetchTasks();
      } catch (error) {
        console.warn(
          'Failed to refresh patient tasks after medication order',
          error
        );
      }
    },
    [fetchTasks, id, navigate, showSuccess]
  );

  const [medicationCount, setMedicationCount] = useState(0);
  const [allergyCount, setAllergyCount] = useState(0);
  const [documentCount, setDocumentCount] = useState(0);
  const [woundCount, setWoundCount] = useState(0);
  // Visit-id → distinct wounds with activity (added / assessed / discontinued).
  // Drives the Wounds-tab picker suffixes; kept separate from woundCount so
  // assessment saves can refresh labels without touching the tab badge.
  const [woundActivityByVisitId, setWoundActivityByVisitId] = useState<
    Record<string, number>
  >({});

  // Guards against out-of-order count refreshes: two mutations in quick
  // succession issue overlapping refreshCounts fetches, and without this a slower
  // earlier response could overwrite a newer count. Only the latest request wins.
  const countsRequestRef = useRef(0);
  const refreshCounts = useCallback(async () => {
    if (!id) return;
    const requestId = ++countsRequestRef.current;
    try {
      const [
        medsRes,
        allergiesRes,
        importedMedsRes,
        importedAllergiesRes,
        woundsRes,
        patientDocsRes,
        intakesRes,
      ] = await Promise.all([
        // The whole list, filtered for "active today" below. The server's
        // include_discontinued flag is presence-based, so it would drop a
        // medication with a planned future stop — one the patient is still
        // taking, and one the list underneath the badge still shows.
        getPatientMedications({
          path: { patient_id: id },
          query: { include_discontinued: true },
        }),
        getPatientAllergies({
          path: { patient_id: id },
        }),
        getImportedMedicationLines({
          path: { patient_id: id },
        }),
        getImportedAllergyProfile({
          path: { patient_id: id },
        }),
        getPatientWounds({
          path: { patient_id: id },
          query: { include_discontinued: false },
        }),
        getPatientDocuments({
          path: { patient_id: id },
          query: { include_task_documents: true },
        }),
        getIntakes({
          query: { patient_id: id },
        }),
      ]);

      const intakeDocumentCount =
        intakesRes?.data?.items?.reduce(
          (total, intake) =>
            total +
            (intake.documents?.filter(d => !d.task_type_tag).length ?? 0),
          0
        ) ?? 0;
      // A newer refresh superseded this one while it was in flight; drop it.
      if (requestId !== countsRequestRef.current) return;
      // Raw imported lines render in the med/allergy lists until someone
      // rules on them, so the badges count them too — a badge that disagrees
      // with the visible rows reads as a bug.
      setMedicationCount(
        countActiveMedications(medsRes?.data) +
          outstandingLines(importedMedsRes?.data?.lines).length
      );
      setAllergyCount(
        (allergiesRes?.data?.length ?? 0) +
          outstandingLines(importedAllergiesRes?.data?.lines).length
      );
      setWoundCount(woundsRes?.data?.length ?? 0);
      const patientDocCount =
        patientDocsRes?.data?.filter(d => d.document_type !== 'intake')
          .length ?? 0;
      setDocumentCount(patientDocCount + intakeDocumentCount);
    } catch {
      // Counts are non-critical
    }
  }, [id]);

  const woundActivityRequestRef = useRef(0);
  const refreshWoundVisitActivity = useCallback(async () => {
    if (!id) return;
    const requestId = ++woundActivityRequestRef.current;
    try {
      const resp = await getPatientWoundVisitActivity({
        path: { patient_id: id },
      });
      if (requestId !== woundActivityRequestRef.current) return;
      // Keep the last good map on a soft client error — wiping to {} would
      // briefly strip every "(N wounds)" suffix until the next successful fetch.
      if (resp.error || !resp.data) return;
      const next: Record<string, number> = {};
      for (const item of resp.data) {
        next[item.visit_id] = item.wound_count;
      }
      setWoundActivityByVisitId(next);
    } catch {
      // Picker suffixes are non-critical
    }
  }, [id]);

  // Clear activity map when the patient changes so a prior chart never
  // briefly labels another patient's visits.
  useEffect(() => {
    setWoundActivityByVisitId({});
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'wounds') return;
    void refreshWoundVisitActivity();
  }, [activeTab, refreshWoundVisitActivity]);

  // Recompute counts on mount, patient change, and tab switch. Task-document
  // uploads and deletes call the same callback to keep the Documents badge current.
  // The badges live on the chart's tab bar, so a task detail URL has no use for
  // them until the user returns.
  useEffect(() => {
    if (visitIdParam) return;
    void refreshCounts();
  }, [refreshCounts, activeTab, visitIdParam]);

  // Refresh the Documents badge the moment a background document upload for this
  // patient completes — patientUploadTick is bumped by every patient- or
  // task-scoped upload. This is decoupled from the uploader's component
  // lifecycle, so it fires even though the paperclip popover closes on upload
  // and the Documents-tab +Add has no direct callback here.
  const patientUploadTick = useStore(
    state => state.patientUploadTick[id ?? ''] ?? 0
  );
  const prevPatientUploadTickRef = useRef(0);
  useEffect(() => {
    if (patientUploadTick !== prevPatientUploadTickRef.current) {
      prevPatientUploadTickRef.current = patientUploadTick;
      // Only the badge is PatientDetail's job here. The Documents list refetches
      // itself via its own tick effect when the tab is mounted; when the tab is
      // closed the ref is null anyway, so a refetch here would be redundant.
      void refreshCounts();
    }
  }, [patientUploadTick, refreshCounts]);

  // Visit-scoped Goals tab badge. Extracted into a hook so the request-id race
  // guard and reset-to-0 paths stay unit-testable; `refreshActiveGoalCount` lets a
  // goal mutation in GoalsTab refresh the badge immediately (ROG-3196).
  // Keyed off the visit the Goals tab actually renders, not the raw selection:
  // a visit from an episode we have switched away from is no longer goal
  // context, so its count must not stay on the badge over an empty tab.
  const { activeGoalCount, refreshActiveGoalCount } = useActiveGoalCount(
    goalVisitTask?.id ?? null,
    tasks,
    activeTab
  );

  useEffect(() => {
    // Refresh the badge when the episode changes and whenever task/visit data
    // refreshes (creating/editing/deleting visits changes authorization errors).
    void fetchAuthErrors();
  }, [fetchAuthErrors, tasksRefreshVersion]);

  // A fix link to Patient Info names the field to correct: focus it once the
  // patient has loaded, then drop the request so a reload does not refocus.
  const fixFieldId = searchParams.get(FIX_FIELD_PARAM);
  const cancelFixFocus = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelFixFocus.current?.(), []);
  useEffect(() => {
    if (!fixFieldId || loading) return;
    cancelFixFocus.current = focusFixField(fixFieldId);
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete(FIX_FIELD_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [fixFieldId, loading, setSearchParams]);

  if (loading) {
    return (
      <PageLoading
        message={
          visitIdParam
            ? 'Loading visit details...'
            : 'Loading patient details...'
        }
      />
    );
  }

  if (error || !patient) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Patient</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Patient not found'}
          </p>
          <Button onClick={() => navigate('/patients')} variant='outline'>
            <ArrowLeft className='h-4 w-4' />
            Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  const tabItems: Array<{
    id: PatientTab;
    label: string;
    count?: number;
    isError?: boolean;
    errorMessages?: string[];
  }> = [
    { id: 'visits', label: 'Tasks', count: tasksForEpisode.length },
    { id: 'documents', label: 'Documents', count: documentCount },
    {
      id: 'medications',
      label: 'Medications',
      count: medicationCount,
    },
    { id: 'allergies', label: 'Allergies', count: allergyCount },
    { id: 'wounds', label: 'Wounds', count: woundCount },
    { id: 'vitals', label: 'Vitals' },
    { id: 'goals', label: 'Goals', count: activeGoalCount },
    {
      id: 'insurance',
      label: 'Insurance',
      count: eligibilityProblemCount > 0 ? eligibilityProblemCount : undefined,
      isError: true,
      errorMessages: eligibilityProblems.map(e => e.message),
    },
    {
      id: 'authorizations',
      label: 'Auths',
      count: authorizationErrorCount > 0 ? authorizationErrorCount : undefined,
      isError: true,
      errorMessages: authorizationErrors.map(e => e.message),
    },
    { id: 'events', label: 'Events' },
    { id: 'info', label: 'Patient Info' },
    { id: 'admission', label: 'Admission' },
    ...(canViewBilling && selectedEpisode
      ? [{ id: 'hhrg-worksheet' as const, label: 'HHRG Worksheet' }]
      : []),
  ];

  // Desktop underline tab. Mobile uses a compact section dropdown instead
  // of wrapping pills (EMR-1427), matching the OASIS section picker.
  const renderTabControl = (tab: (typeof tabItems)[number]) => {
    const isActive = activeTab === tab.id;
    const showErrorTooltip = Boolean(tab.isError && tab.errorMessages?.length);
    const tabButton = (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        className={`shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 transition-all ${
          isActive
            ? 'border-primary text-foreground'
            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border hover:bg-muted/30'
        }`}
      >
        {tab.label}
        {tab.count != null && tab.count > 0 && (
          <Badge
            variant={tab.isError ? 'destructive' : 'secondary'}
            className='ml-1.5 text-[10px] px-1.5 py-0 h-4 min-w-4 inline-flex items-center justify-center'
          >
            {tab.count}
          </Badge>
        )}
      </button>
    );
    // Surface auth errors when hovering anywhere over the tab, not just the badge.
    return showErrorTooltip ? (
      <TooltipProvider key={tab.id}>
        <Tooltip>
          <TooltipTrigger asChild>{tabButton}</TooltipTrigger>
          <TooltipContent>
            <ul className='list-none space-y-0.5 text-xs'>
              {(tab.errorMessages ?? []).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      tabButton
    );
  };
  const addEpisodeControl =
    activeTab === 'visits' && !isExternalEmrShell ? (
      <span className='inline-flex flex-col items-start gap-1'>
        <Button
          size='sm'
          variant='outline'
          disabled={!canAddEpisode}
          onClick={() => setAddEpisodeOpen(true)}
          title={
            canAddEpisode ? undefined : (addEpisodeBlockedMessage ?? undefined)
          }
        >
          <Plus className='h-4 w-4' />
          Add Episode
        </Button>
        {!canAddEpisode && addEpisodeBlockedMessage && (
          <span className='text-xs text-muted-foreground'>
            {addEpisodeBlockedMessage}
          </span>
        )}
      </span>
    ) : null;

  // The detail view of a deleted task is read-only, so restore is the only
  // action it offers. It sits above the panel rather than inside each of the
  // four, so every task type gets the same control and the same refusal.
  const deletedTaskRestoreBanner =
    selectedTaskIsDeleted && selectedTask ? (
      <div className='flex flex-wrap items-center justify-between gap-2 border-b border-destructive/30 bg-destructive/5 px-3 py-2'>
        <span className='text-sm font-medium text-destructive'>
          This task is deleted.
        </span>
        <RestoreTaskButton
          task={selectedTask}
          taskLabel={getTaskDisplayName(selectedTask) || 'task'}
          restoring={restoringTaskId === selectedTask.id}
          onRestore={() =>
            void handleRestoreTask(selectedTask, { closeDetail: true })
          }
        />
      </div>
    ) : null;

  // A shell is a patient known from the External EMR census, with no chart in
  // Roger yet. Its page is the header and the import panel only: no tabs, no
  // Add Task and no chart actions, so nothing can be written to a chart that
  // does not exist.
  if (isExternalEmrShell && externalEmr && id) {
    return (
      <div className='bg-background flex flex-col min-h-full'>
        <PatientHeader patient={patient} insurances={patientInsurances} />
        <ExternalEmrChartPanel
          patientId={id}
          external={externalEmr}
          canStart={canStartExternalEmrImport}
          importBlockedReason={externalEmrImportBlockedReason}
          importReadinessPending={!externalEmrImportReadinessLoaded}
          recertDue={false}
          onAddRecertEpisode={() => setAddEpisodeOpen(true)}
          onChanged={handleExternalEmrChanged}
          importedAt={patient?.imported_at ?? null}
        />
      </div>
    );
  }

  // From `md` up, detail panels own their own scrolling: an `h-full` root over
  // a `flex-1 overflow-auto` body. That only works when every parent has a
  // definite height, so bound this page to the viewport while a panel is open.
  // Without it the panel's scroll box just grows to content height, the page
  // scroller takes over, and nothing inside the panel can pin itself.
  //
  // Deliberately not applied on mobile. Bounding the page there pins the
  // patient and visit headers too, and at phone heights that chrome eats
  // roughly half the screen. Mobile keeps scrolling the page so both headers
  // scroll away, and the form's own pinned bar sticks to the page scroller
  // instead. The episode and tasks branch has no scroll box of its own and
  // grows into the page scroller at every width.
  //
  // While bounded, the page also runs into the layout `<main>`'s bottom
  // padding. Cancel only --layout-main-pb-base (md:p-6 / 1.5rem): the panel
  // scrolls inside itself, so that padding would only leave an empty strip
  // under the form. Chat clearance is additive on the base (index.css), so
  // canceling the base still leaves room for the Messages launcher.
  return (
    <div
      className={`bg-background flex flex-col ${
        canRenderSelectedTaskDetail
          ? 'min-h-full md:h-[calc(100%+var(--layout-main-pb-base))] md:-mb-[var(--layout-main-pb-base)]'
          : 'min-h-full'
      }`}
    >
      <PatientHeader
        patient={patient}
        insurances={patientInsurances}
        episodePatientInsuranceId={
          selectedEpisode?.patient_insurance_id ?? null
        }
        onEditEpisodeInsurance={() => {
          setIsEpisodeInsuranceEditorOpen(true);
        }}
        episodeId={selectedEpisode?.id ?? null}
        episodeChartsFrom={
          selectedEpisode
            ? {
                start_date: selectedEpisode.start_date,
                end_date: selectedEpisode.end_date,
                roger_charts_from: selectedEpisode.roger_charts_from,
              }
            : null
        }
        onExternalEmrHistorySettled={handleExternalEmrHistorySettled}
        // The Refresh button's own gate in ExternalEmrChartPanel.
        canRefreshExternalEmr={
          canStartExternalEmrImport &&
          externalEmrImportReadinessLoaded &&
          !externalEmrImportBlockedReason
        }
        actions={
          <Button
            variant='outline'
            size='sm'
            onClick={() => setIsFaceSheetOpen(true)}
          >
            <ClipboardList className='h-4 w-4' />
            Summary
          </Button>
        }
      />

      {id ? <DuplicateImportNotice patientId={id} /> : null}

      <ManagedInExternalEmrNotice
        show={showImportedBeforeTakeOverWarn}
        message={IMPORTED_CHART_BEFORE_TAKE_OVER_BANNER}
      />

      {id && externalEmr ? (
        <ExternalEmrChartPanel
          patientId={id}
          external={externalEmr}
          canStart={canStartExternalEmrImport}
          importBlockedReason={externalEmrImportBlockedReason}
          importReadinessPending={!externalEmrImportReadinessLoaded}
          recertDue={externalEmrRecertDue}
          takeoverDue={externalEmrTakeoverDue}
          takeOverOffered={takeOverOnImportBar}
          onTakeOver={() => setTakeOverOpen(true)}
          onAddRecertEpisode={() => setAddEpisodeOpen(true)}
          onChanged={handleExternalEmrChanged}
          importedAt={patient?.imported_at ?? null}
        />
      ) : null}

      {/* Take Over v2 only: the server sends the move only with the
          take-over flag on, and the basic Take Over never showed this. */}
      {takeOverV2 && chartMovedMoment ? (
        <ChartMovedNotice
          movedAt={chartMovedMoment}
          via={chartMovedVia}
          chartsFrom={movedEpisode?.roger_charts_from}
          onUndo={undoEpisodeId ? openUndoTakeOver : undefined}
        />
      ) : null}

      <PatientFaceSheet
        patient={patient}
        episode={selectedEpisode ?? null}
        physicians={faceSheetPhysicians}
        insurances={patientInsurances}
        open={isFaceSheetOpen}
        onOpenChange={setIsFaceSheetOpen}
      />

      {/* Main Content */}
      {/* A deleted 30-Day Summary does not reach this branch:
        selectedTaskIsThirtyDaySummary is resolved from the live communications
        list, so a deleted one falls through to the generic communication panel,
        which is the one that reads deleted records and carries the restore
        banner. That panel recognises a summary and renders its print view, so
        the clinical content survives the detour; sending it here instead would
        mean making an editing panel that autosaves safe to open read-only. */}
      {canRenderSelectedTaskDetail && selectedTaskIsThirtyDaySummary ? (
        <div className='flex-1 flex flex-col min-h-0'>
          <ThirtyDaySummaryDetailPanel
            communicationId={selectedVisitId}
            patientId={id}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
          />
        </div>
      ) : canRenderSelectedTaskDetail && selectedTask?.task_type === 'order' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          {deletedTaskRestoreBanner}
          {managedTaskBanner}
          <OrderDetailPanel
            orderId={selectedVisitId}
            patientId={id}
            isDeleted={selectedTaskIsDeleted}
            onVisitUpdated={handleVisitUpdated}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
          />
        </div>
      ) : canRenderSelectedTaskDetail &&
        selectedTask?.task_type === 'patient_communication' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          {deletedTaskRestoreBanner}
          <CommunicationDetailPanel
            // Remount per communication, same as the misc-task panel below.
            // This panel's useAutoSave sits at its top level, ABOVE the
            // `if (loading)` return, so swapping the id in place keeps the
            // hook (and its unsaved draft) alive across the switch: the new
            // record's data would be refused as an external value while the
            // previous one's edit is still dirty, leaving record A's notes on
            // screen under record B — and writing them there on the next edit.
            key={selectedVisitId}
            communicationId={selectedVisitId}
            patientId={id}
            isDeleted={selectedTaskIsDeleted}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
            onUpdated={() => void fetchTasks()}
          />
        </div>
      ) : canRenderSelectedTaskDetail &&
        selectedTask?.task_type === 'misc_task' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          {deletedTaskRestoreBanner}
          {managedTaskBanner}
          <MiscTaskDetailPanel
            // Remount per task. The panel guards its own loads against a task
            // switch, but keying it means a new task cannot start from the
            // previous one's state at all.
            key={selectedVisitId}
            miscTaskId={selectedVisitId}
            patientId={id}
            isDeleted={selectedTaskIsDeleted}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
            onUpdated={() => void fetchTasks()}
          />
        </div>
      ) : canRenderSelectedTaskDetail ? (
        <div className='flex-1 flex flex-col min-h-0'>
          {deletedTaskRestoreBanner}
          <VisitDetailPanel
            visitId={selectedVisitId}
            patientId={id}
            patient={patient}
            isDeleted={selectedTaskIsDeleted}
            fixTarget={chartFixTarget}
            onFixTargetHandled={clearChartFixTarget}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
            onVisitUpdated={handleVisitUpdated}
            physicians={getClinicPhysicians(patient?.clinic_id)}
          />
        </div>
      ) : visitIdParam && selectedTaskPending ? (
        <PageLoading message='Loading visit details...' />
      ) : visitIdParam && selectedTaskLookupFailed ? (
        <div className='flex items-center justify-center h-64'>
          <div className='text-center'>
            <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
            <p className='text-muted-foreground mb-4'>
              Unable to load this visit.
            </p>
            <Button onClick={retrySelectedTaskLookup} variant='outline'>
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <div className='p-2 sm:p-5 max-lg:p-0'>
          <EpisodeManagedInExternalEmrNotice episode={selectedEpisode} />
          <Card className='border-border/70 shadow-sm max-lg:rounded-none max-lg:border-0 max-lg:p-0 max-lg:shadow-none'>
            <CardHeader className='max-lg:px-3 max-lg:pt-2'>
              <div className='grid grid-cols-1 sm:grid-cols-[1fr_auto] items-start gap-3 max-lg:gap-2'>
                <CardDescription className='min-w-0 text-left'>
                  {selectedEpisode ? (
                    <span className='flex flex-col items-start gap-1'>
                      <span className='flex flex-wrap items-center gap-1.5'>
                        <span>
                          Episode: {formatDate(selectedEpisode.start_date)} –{' '}
                          {formatDate(selectedEpisode.end_date)}
                        </span>
                        {canEditSelectedEpisode && (
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            aria-label='Edit episode'
                            onClick={() => setIsEpisodeEditorOpen(true)}
                          >
                            <Pencil className='h-4 w-4' />
                          </Button>
                        )}
                        {selectedEpisode.status && (
                          <Badge
                            variant={episodeStatusBadgeVariant(
                              selectedEpisode.status
                            )}
                            className='text-[10px] capitalize leading-normal'
                          >
                            {episodeStatusDisplayLabel(selectedEpisode.status)}
                          </Badge>
                        )}
                        {selectedEpisode.billed_externally && (
                          <ExternallyBilledBadge className='text-[10px] leading-normal' />
                        )}
                        {selectedEpisode.billed_externally &&
                          canUnsetBilledExternallyMigration && (
                            <UnsetBilledExternallyButton
                              episodeId={selectedEpisode.id}
                              onSuccess={() => {
                                void loadEpisodes();
                                showSuccess(
                                  'Flag cleared',
                                  'Billed externally was cleared for this episode.'
                                );
                              }}
                              onError={showError}
                            />
                          )}
                        {canTakeOverSelectedEpisode && (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => setTakeOverOpen(true)}
                            data-testid='episode-take-over'
                          >
                            Take over
                          </Button>
                        )}
                        {canUndoSelectedEpisode && (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={openUndoTakeOver}
                            data-testid='episode-undo-take-over'
                          >
                            {UNDO_BUTTON}
                          </Button>
                        )}
                        {otherEpisodes.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant='linkMuted' size='sm'>
                                View {otherEpisodes.length} other episode
                                {otherEpisodes.length === 1 ? '' : 's'}
                                <ChevronDown />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align='start'
                              className='min-w-[260px]'
                            >
                              {otherEpisodes.map(ep => (
                                <DropdownMenuItem
                                  key={ep.id}
                                  onClick={() => setSelectedEpisodeId(ep.id)}
                                  className='flex items-center justify-between gap-3'
                                >
                                  <span>
                                    {formatDate(ep.start_date)} –{' '}
                                    {formatDate(ep.end_date)}
                                  </span>
                                  <span className='flex shrink-0 items-center gap-1'>
                                    {ep.status && (
                                      <Badge
                                        variant={episodeStatusBadgeVariant(
                                          ep.status
                                        )}
                                        className='text-[10px] capitalize leading-normal'
                                      >
                                        {episodeStatusDisplayLabel(ep.status)}
                                      </Badge>
                                    )}
                                    {ep.billed_externally && (
                                      <ExternallyBilledBadge className='text-[10px] leading-normal' />
                                    )}
                                  </span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {addEpisodeControl}
                        <BillingManagerLink patientId={id!} />
                      </span>
                      {selectedEpisode.take_over_undone && (
                        <TakeOverUndoneTrace
                          undone={selectedEpisode.take_over_undone}
                        />
                      )}
                      {/* Always rendered: the clinical manager reads
                          "Unassigned" when the episode has none (EMR-2700). */}
                      <span className='text-xs'>
                        {selectedEpisode.assigned_clinician_name && (
                          <>
                            Clinician:{' '}
                            <UnclaimedClinicianName
                              name={selectedEpisode.assigned_clinician_name}
                              unclaimed={
                                selectedEpisode.assigned_clinician_unclaimed_import ===
                                true
                              }
                              testId='episode-clinician-unclaimed'
                            />
                          </>
                        )}
                        {selectedEpisode.assigned_clinician_name &&
                          selectedEpisode.case_manager_name &&
                          ' · '}
                        {selectedEpisode.case_manager_name && (
                          <>
                            Case Manager:{' '}
                            <UnclaimedClinicianName
                              name={selectedEpisode.case_manager_name}
                              unclaimed={
                                selectedEpisode.case_manager_unclaimed_import ===
                                true
                              }
                              testId='episode-case-manager-unclaimed'
                            />
                          </>
                        )}
                        {(selectedEpisode.assigned_clinician_name ||
                          selectedEpisode.case_manager_name) &&
                          ' · '}
                        Clinical Manager:{' '}
                        {selectedEpisode.clinical_manager_name ? (
                          <UnclaimedClinicianName
                            name={selectedEpisode.clinical_manager_name}
                            unclaimed={
                              selectedEpisode.clinical_manager_unclaimed_import ===
                              true
                            }
                            testId='episode-clinical-manager-unclaimed'
                          />
                        ) : (
                          'Unassigned'
                        )}
                        {selectedEpisode.patient_insurance_name && (
                          <>
                            {' · '}
                            Insurance: {selectedEpisode.patient_insurance_name}
                          </>
                        )}
                      </span>
                    </span>
                  ) : (
                    <span className='flex flex-wrap items-start gap-2'>
                      <span>Episode tasks and assignments</span>
                      {addEpisodeControl}
                    </span>
                  )}
                </CardDescription>
                {(selectedEpisode || activeTab === 'visits') && (
                  <div className='flex flex-wrap justify-start sm:justify-end gap-2 max-lg:gap-1.5'>
                    {selectedEpisode && (
                      <>
                        <CareTeamChatButton episodeId={selectedEpisode.id} />
                        {canClaimSelectedEpisodeWorksheet && (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => setIsEpisodeWorksheetOpen(true)}
                          >
                            Claim worksheet
                          </Button>
                        )}
                      </>
                    )}
                    {activeTab === 'visits' && addTaskTargetEpisode && (
                      // Visit-only dialog: disable when the episode refuses
                      // visits (cancelled/interrupted). Keep visible with the
                      // reason beside it — disabled buttons have no tooltip.
                      <span className='inline-flex flex-col items-start gap-1'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => setIsScheduleVisitsOpen(true)}
                          disabled={targetEpisodeBlocksVisits || chartLocked}
                        >
                          <CalendarPlus className='h-4 w-4' />
                          Schedule visits
                        </Button>
                        {targetEpisodeBlocksVisits && (
                          <span className='text-xs text-muted-foreground'>
                            {targetEpisodeClosedLocked
                              ? closedEpisodeCopy(canAmendClosedEpisode)
                                  .headline
                              : 'Episode cannot accept visits'}
                          </span>
                        )}
                      </span>
                    )}
                    {activeTab === 'visits' && (
                      <Button
                        size='sm'
                        onClick={() => openAddTaskDialog()}
                        disabled={chartLocked}
                      >
                        <Plus className='h-4 w-4' />
                        Add Task
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>

            {/* Tab Navigation. Mobile uses a compact section dropdown (same
                idea as OASIS) so ten wrapping pills do not eat the viewport;
                desktop keeps the underline bar. Rendered one-at-a-time (not
                via CSS hidden) so only one set of tab controls is in the DOM. */}
            {isMobile ? (
              <div>
                <Select
                  value={activeTab}
                  onValueChange={value => {
                    if (isValidTab(value)) {
                      setActiveTab(value);
                    }
                  }}
                >
                  <SelectTrigger
                    selectSize='sm'
                    className='w-full rounded-none border-0 border-b border-border/60 px-3 shadow-none'
                    aria-label='Patient section'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='w-[var(--radix-select-trigger-width)]'>
                    {tabItems.map(tab => {
                      const countSuffix =
                        tab.count != null && tab.count > 0
                          ? ` (${tab.count})`
                          : '';
                      return (
                        <SelectItem key={tab.id} value={tab.id}>
                          {tab.isError && tab.errorMessages?.length
                            ? `⚠ ${tab.label}${countSuffix}`
                            : `${tab.label}${countSuffix}`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div
                data-testid='patient-chart-tabs'
                className='border-b border-border/60 px-6 flex items-center gap-1.5 overflow-x-auto'
              >
                {tabItems.map(tab => renderTabControl(tab))}
              </div>
            )}

            <CardContent className='pt-4 max-lg:px-3 max-lg:pt-2'>
              {/* ─── Tasks Tab ─── */}
              {activeTab === 'visits' && (
                <TasksTab
                  patientId={id!}
                  patientLabel={
                    patient
                      ? `${patient.first_name} ${patient.last_name}`
                      : undefined
                  }
                  effectiveSelectedEpisodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                  rogerChartsFrom={selectedEpisode?.roger_charts_from}
                  calendarTasks={calendarTasks}
                  clinicians={clinicians}
                  duplicateClinicianNames={duplicateClinicianNames}
                  lupaPeriods={lupaPeriods}
                  lupaLoading={lupaLoading}
                  authorizationBudget={episodeAuthBudget}
                  authorizationBudgetLoading={authBudgetLoading}
                  requiresAuthorization={episodeRequiresAuthorization}
                  unlinkedVisitCount={
                    authorizationErrors.find(
                      e => e.code === AUTHORIZATION_ERROR_CODE.UNLINKED_VISITS
                    )?.visit_count ?? 0
                  }
                  authorizationBudgetUnavailable={authBudgetUnavailable}
                  visitsLoading={visitsLoading}
                  visitsError={visitsError}
                  tasksForEpisode={tasksForEpisode}
                  displayedTasks={displayedTasks}
                  deletedTasks={deletedTasks}
                  deletedTaskById={deletedTaskById}
                  deletedTasksLoading={deletedTasksLoading}
                  deletedTasksError={deletedTasksError}
                  deletedTasksReady={deletedTasksReady}
                  taskView={taskView}
                  onTaskViewChange={setTaskView}
                  tableTaskTypeFilter={tableTaskTypeFilter}
                  onTableTaskTypeFilterChange={setTableTaskTypeFilterFromUser}
                  selectedTaskIds={selectedTaskIds}
                  selectedTasks={selectedTasks}
                  selectAllState={selectAllState}
                  atSelectionCap={atSelectionCap}
                  onToggleTaskSelected={toggleTaskSelected}
                  onToggleSelectAll={handleToggleSelectAll}
                  onClearTaskSelection={clearTaskSelection}
                  onRemoveTaskFromSelection={removeTaskFromSelection}
                  batchActions={batchActions}
                  getTaskDisplayName={getTaskDisplayName}
                  getTaskAssigneeLabel={getTaskAssigneeLabel}
                  getTaskAssigneeClinicians={getTaskAssigneeClinicians}
                  canEditEpisodeManagerTask={canEditEpisodeManagerTask}
                  formatDuplicateClinicianOptionLabel={
                    formatDuplicateClinicianOptionLabel
                  }
                  taskTargetDateBounds={taskTargetDateBounds}
                  updatingTaskId={updatingTaskId}
                  rejectedEdit={rejectedEdit}
                  showEvvColumn={showEvvColumn}
                  canViewSchedulingPointLoads={canViewSchedulingPointLoads}
                  restoringTaskId={restoringTaskId}
                  printTask={printTask}
                  printingTaskId={printingTaskId}
                  printDeletedTask={printDeletedTask}
                  printingDeletedTaskId={printingDeletedTaskId}
                  onTaskOpen={handleTaskOpen}
                  onAssigneeChange={handleAssigneeChange}
                  onTargetDateChange={handleTargetDateChange}
                  onCreateTaskFromDate={openAddTaskDialog}
                  onRequestDelete={setDeleteTask}
                  onRestoreTask={task => void handleRestoreTask(task)}
                  onOpenEvv={setEvvVisitId}
                  onDocumentsChanged={() => {
                    void fetchTasks({ silent: true });
                    void refreshCounts();
                  }}
                />
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <PatientDocumentsTab
                  // Keyed so the tab starts clean on patient navigation: it owns
                  // its own document fetches, signed URLs and scope choice, and
                  // none of them are patient-agnostic. Without this the previous
                  // patient's documents can paint under this patient's episode.
                  key={id}
                  ref={documentsRef}
                  patientId={id!}
                  patientLabel={
                    patient
                      ? `${patient.first_name} ${patient.last_name}`
                      : undefined
                  }
                  documentContextTasks={documentContextTasks}
                  episodeScope={documentsEpisodeScope}
                  chartLocked={chartLocked}
                />
              )}

              {/* ─── Medications Tab ─── */}
              {activeTab === 'medications' && (
                <PatientMedications
                  patientId={id!}
                  episodeId={selectedEpisode?.id}
                  visitId={selectedVisitTask?.id}
                  visitStatus={selectedVisitTask?.status}
                  episodeClosed={selectedVisitTask?.episode_closed}
                  chartLocked={chartLocked}
                  visitSelector={
                    <div className='flex w-full flex-wrap items-center gap-2 sm:w-auto'>
                      <VisitTaskPicker
                        items={[...episodeVisitTasks].sort(
                          (a, b) =>
                            (parseISOToLocalDate(
                              b.task_date ?? b.scheduled_date
                            )?.getTime() ?? 0) -
                            (parseISOToLocalDate(
                              a.task_date ?? a.scheduled_date
                            )?.getTime() ?? 0)
                        )}
                        valueId={selectedVisitTask?.id ?? null}
                        onValueIdChange={id => setSelectedVisitId(id)}
                        placeholder='Select visit for MAR'
                        selectTriggerClassName='w-full sm:w-[300px]'
                        renderItemLabel={task => (
                          <>
                            {task.display_name} -{' '}
                            {formatDate(task.task_date ?? task.scheduled_date)}
                          </>
                        )}
                        emptyHelper={
                          <span className='text-xs text-muted-foreground'>
                            Select a visit to record administrations
                          </span>
                        }
                      />
                    </div>
                  }
                  onMedicationOrderCreated={handleMedicationOrderCreated}
                  onDocumentCreated={() =>
                    documentsRef.current?.refreshDocuments()
                  }
                  onMedicationsChanged={() => void refreshCounts()}
                />
              )}

              {/* Allergies Tab */}
              {activeTab === 'allergies' && (
                <PatientAllergies
                  patient={patient}
                  onAllergiesChanged={() => void refreshCounts()}
                  onPatientChanged={() => void refreshPatient()}
                />
              )}

              {/* Goals still require a visit before the list can open. Wounds
                  keep their picker inside the card, next to the actions it
                  enables, the same way Medications does. */}
              {activeTab === 'goals' && (
                <div className='mb-4 mt-1'>
                  {canContinueGoals && effectiveSelectedEpisodeId && (
                    <div className='mb-2'>
                      <ContinueGoalsFromPreviousEpisode
                        // Remount per episode so the offer is re-read.
                        key={effectiveSelectedEpisodeId}
                        episodeId={effectiveSelectedEpisodeId}
                        onContinued={() => {
                          void refreshActiveGoalCount();
                          setKinnserGoalsVersion(v => v + 1);
                        }}
                      />
                    </div>
                  )}
                  {/* With no eligible visit there is nothing to pick, so the tab
                      body carries the single explanatory message instead. The
                      external-EMR goals button still renders there: the
                      migration flow exists exactly because such episodes can
                      have no goal-eligible visit. */}
                  {canAddKinnserGoals &&
                    effectiveSelectedEpisodeId &&
                    goalVisitTasks.length === 0 && (
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => setIsKinnserGoalsOpen(true)}
                        className='flex items-center gap-1'
                      >
                        <Plus className='h-4 w-4' />
                        Add Goals from External EMR
                      </Button>
                    )}
                  {goalVisitTasks.length > 0 && (
                    <>
                      <div className='flex flex-wrap items-center gap-2'>
                        <VisitTaskPicker
                          items={[...goalVisitTasks].sort(
                            (a, b) =>
                              new Date(
                                b.task_date ?? b.scheduled_date
                              ).getTime() -
                              new Date(
                                a.task_date ?? a.scheduled_date
                              ).getTime()
                          )}
                          valueId={goalVisitTask?.id ?? null}
                          onValueIdChange={id => setSelectedVisitId(id)}
                          placeholder='Select visit'
                          selectTriggerClassName={`w-full sm:w-[360px] ${
                            !goalVisitTask
                              ? 'border-destructive/60 text-destructive'
                              : ''
                          }`}
                          renderItemLabel={task => (
                            <>
                              {task.display_name} -{' '}
                              {formatDate(
                                task.task_date ?? task.scheduled_date
                              )}
                            </>
                          )}
                        />
                        {canAddKinnserGoals && effectiveSelectedEpisodeId && (
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() => setIsKinnserGoalsOpen(true)}
                            className='flex items-center gap-1'
                          >
                            <Plus className='h-4 w-4' />
                            Add Goals from External EMR
                          </Button>
                        )}
                      </div>
                      {!goalVisitTask && (
                        <p className='mt-2 inline-flex items-center gap-1.5 text-xs text-destructive'>
                          <AlertCircle className='h-3.5 w-3.5' />
                          Select a visit to enable goals.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Wounds Tab */}
              {activeTab === 'wounds' && (
                <PatientWounds
                  patientId={id!}
                  visitId={woundVisitTask?.id}
                  visitStatus={woundVisitTask?.status}
                  episodeClosed={woundVisitTask?.episode_closed}
                  chartLocked={chartLocked}
                  // Chart tab only — visit notes stay a flat table so Live
                  // Scrubber can jump to discontinued rows (EMR-931).
                  splitByStatus
                  // Unfiltered on purpose: this is the lookup the assessments
                  // drawer uses to name the visit each assessment was recorded
                  // on, including visits in an earlier episode and visits whose
                  // type no longer has a wound section. Wounds are patient
                  // history, so the names have to outlive the episode on
                  // screen. The picker above is what gets filtered.
                  visits={visitTasks}
                  onWoundsChanged={() => {
                    void refreshCounts();
                    void refreshWoundVisitActivity();
                  }}
                  onWoundActivityChanged={() => {
                    void refreshWoundVisitActivity();
                  }}
                  // Wounds are patient-scoped history, so the list stays
                  // readable with no visit selected. This banner is only for
                  // the case where no visit type can ever serve as context.
                  noVisitContextReason={
                    woundVisitTasks.length === 0
                      ? `${
                          selectedEpisode
                            ? 'No visits in this episode have a wounds section.'
                            : 'This patient has no episode yet, so there is no visit to assess a wound on.'
                        } Existing wounds can be corrected or removed here, but not added or assessed.`
                      : undefined
                  }
                  visitSelector={
                    woundVisitTasks.length > 0 ? (
                      <div className='flex min-w-0 flex-wrap items-center gap-2'>
                        <VisitTaskPicker
                          items={[...woundVisitTasks].sort(
                            (a, b) =>
                              new Date(
                                b.task_date ?? b.scheduled_date
                              ).getTime() -
                              new Date(
                                a.task_date ?? a.scheduled_date
                              ).getTime()
                          )}
                          valueId={woundVisitTask?.id ?? null}
                          onValueIdChange={id => setSelectedVisitId(id)}
                          placeholder='Select visit'
                          selectTriggerClassName='w-full sm:w-[300px]'
                          renderItemLabel={task => (
                            <>
                              {task.display_name} -{' '}
                              {formatDate(
                                task.task_date ?? task.scheduled_date
                              )}
                              {formatWoundVisitActivitySuffix(
                                woundActivityByVisitId[task.id] ?? 0
                              )}
                            </>
                          )}
                          emptyHelper={
                            <span className='text-xs text-muted-foreground'>
                              Select a visit to enable wound actions
                            </span>
                          }
                        />
                      </div>
                    ) : undefined
                  }
                />
              )}

              {/* Goals Tab */}
              {activeTab === 'goals' && (
                <GoalsChartsFromNote episode={selectedEpisode} />
              )}
              {activeTab === 'goals' &&
                (goalVisitTask ? (
                  <div className='h-[calc(100vh-10rem)]'>
                    <GoalsTab
                      // Remount when the Kinnser dialog saves a goal so the
                      // list refetches and shows it.
                      key={`${goalVisitTask.id}-${kinnserGoalsVersion}`}
                      open={true}
                      onClose={() => {}}
                      patientFirstName={patient.first_name}
                      patientLastName={patient.last_name}
                      visit={goalVisitTask}
                      inline={true}
                      capabilities={visitGoalCapabilities(
                        goalVisitTask.visit_type as VisitTypeId
                      )}
                      discipline={getDisciplineForVisitType(
                        goalVisitTask.visit_type
                      )}
                      onGoalsChanged={() => void refreshActiveGoalCount()}
                    />
                  </div>
                ) : (
                  <div className='flex flex-col items-center justify-center py-12 text-muted-foreground'>
                    <Target className='h-12 w-12 mb-4 opacity-30' />
                    <p className='text-base font-medium'>
                      {goalVisitTasks.length > 0
                        ? 'Select a visit to get started.'
                        : selectedEpisode
                          ? 'No visits in this episode have a goals section.'
                          : 'This patient has no episode yet, so there is no visit to set a goal on.'}
                    </p>
                  </div>
                ))}
              {canAddKinnserGoals && selectedEpisode && (
                <AddExternalEmrGoalsDialog
                  open={isKinnserGoalsOpen}
                  onClose={() => setIsKinnserGoalsOpen(false)}
                  episodeId={selectedEpisode.id}
                  episodeStartDate={selectedEpisode.start_date}
                  episodeEndDate={selectedEpisode.end_date}
                  onGoalAdded={() => {
                    void refreshActiveGoalCount();
                    setKinnserGoalsVersion(v => v + 1);
                  }}
                />
              )}

              {/* ─── HHRG Worksheet Tab ─── */}
              {activeTab === 'hhrg-worksheet' &&
                canViewBilling &&
                selectedEpisode && (
                  <HhrgWorksheetTab
                    episode={selectedEpisode}
                    patientName={`${patient.first_name} ${patient.last_name}`}
                  />
                )}

              {/* ─── Authorizations Tab ─── */}
              {activeTab === 'authorizations' && (
                <AuthorizationsTab
                  patientId={id!}
                  episodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                  defaultInsuranceId={selectedEpisode?.patient_insurance_id}
                  onErrorsChanged={fetchAuthErrors}
                />
              )}

              {/* ─── Vitals Tab (EMR-2535; nested-ready shell) ─── */}
              {activeTab === 'vitals' && (
                <VitalsTab
                  patientId={id!}
                  episodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                />
              )}

              {/* ─── Events Tab ─── */}
              {activeTab === 'events' && (
                <EventsTab
                  patientId={id!}
                  episodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                  episodes={episodes}
                  clinicians={assignableClinicians}
                  physicians={getClinicPhysicians(patient?.clinic_id)}
                />
              )}

              {/* ─── Insurance Tab ─── */}
              {activeTab === 'insurance' && (
                <InsuranceTab
                  patientId={patient.id}
                  onAddInsurance={() => {
                    setEditingInsuranceId(null);
                    setInsurancePopupOpen(true);
                  }}
                  onEditInsurance={insuranceId => {
                    setEditingInsuranceId(insuranceId);
                    setInsurancePopupOpen(true);
                  }}
                  insuranceRefreshTrigger={insuranceRefreshTrigger}
                  onInsurancesChanged={() => void refreshPatientInsurances()}
                  onEligibilityChanged={() => void fetchEligibilityProblems()}
                />
              )}

              {/* ─── Patient Info Tab ─── */}
              {activeTab === 'info' && (
                <PatientInfoTab
                  patient={patient}
                  canEditPatientInfo={canEditPatientInfo}
                  onEpisodeAssignmentsChanged={() => {
                    void loadEpisodes();
                  }}
                  onPatientUpdated={handlePatientUpdated}
                />
              )}

              {/* ─── Admission Tab ─── */}
              {activeTab === 'admission' && (
                <AdmissionTab
                  admissionId={selectedEpisode?.admission_id}
                  clinicId={patient?.clinic_id}
                />
              )}
            </CardContent>
          </Card>
          <AddEpisodeModal
            open={addEpisodeOpen}
            onClose={() => setAddEpisodeOpen(false)}
            patientId={id!}
            clinicId={patient.clinic_id ?? ''}
            previousEpisode={previousEpisode}
            existingEpisodes={episodes}
            onCreated={newEpisode => {
              setEpisodes(prev => [
                newEpisode,
                ...prev.filter(episode => episode.id !== newEpisode.id),
              ]);
              setSelectedEpisodeId(newEpisode.id);
              void loadEpisodes();
              // Add Episode may move the chart: re-read its notice and lock.
              void refreshPatient();
            }}
            takeoverCandidate={takeoverCandidate}
            canTakeOver={canTakeOverExternalEmrPeriod}
            takeOverV2={takeOverV2}
            onTakenOver={episode => {
              setEpisodes(prev =>
                prev.map(e => (e.id === episode.id ? episode : e))
              );
              setSelectedEpisodeId(episode.id);
              void loadEpisodes();
              void refreshPatient();
            }}
            onStartTakeOver={() => {
              setAddEpisodeOpen(false);
              setTakeOverOpen(true);
            }}
          />
          <PatientInsuranceFormDialog
            open={insurancePopupOpen}
            onClose={() => {
              setInsurancePopupOpen(false);
              setEditingInsuranceId(null);
              setInsurancesLoading(true);
              void listPatientInsurances({
                path: { patient_id: id! },
              })
                .then(res => {
                  if (res.data) {
                    setPatientInsurances(res.data);
                    setInsuranceRefreshTrigger(prev => prev + 1);
                  }
                })
                .catch(err => {
                  console.error('Failed to load patient insurances:', err);
                })
                .finally(() => {
                  setInsurancesLoading(false);
                });
            }}
            patientId={id!}
            clinicId={patient.clinic_id}
            patient={patient}
            editingInsuranceId={editingInsuranceId}
          />
        </div>
      )}

      {/* Schedule Visits Dialog (bulk create across multiple dates) */}
      {addTaskTargetEpisode && (
        <ScheduleVisitsDialog
          open={isScheduleVisitsOpen}
          onOpenChange={setIsScheduleVisitsOpen}
          patientId={id!}
          episodeId={addTaskTargetEpisode.id}
          episodeStartDate={addTaskTargetEpisode.start_date}
          episodeEndDate={addTaskTargetEpisode.end_date}
          visitTypeOptions={visitTypeOptions}
          clinicians={assignableClinicians}
          duplicateClinicianNames={duplicateAssignableClinicianNames}
          onCreated={() => {
            // Bulk scheduling is the single largest mover of a clinician's
            // week — it can add a dozen visits at once — so it is the last
            // place a stale cached load should survive.
            void invalidateSchedulingPointLoads();
            return fetchTasks();
          }}
          requiresAuthorization={episodeRequiresAuthorization}
          // The LUPA rows and tasks loaded here describe the selected episode,
          // so they are passed only when the dialog schedules into that one.
          {...(addTaskTargetEpisode.id === selectedEpisode?.id && {
            rogerChartsFrom: selectedEpisode.roger_charts_from,
            lupaPeriods,
            lupaLoading,
            // Visits only: the Tasks calendar leaves communications off too.
            episodeVisits: documentContextTasks,
          })}
        />
      )}

      <AddTaskDialog
        open={isAddDialogOpen}
        onOpenChange={open => {
          if (open) {
            openAddTaskDialog();
            return;
          }
          setIsAddDialogOpen(false);
        }}
        initialDate={addTaskInitialDate}
        initialFilter={tableTaskTypeFilter}
        patientId={id!}
        addTaskTargetEpisode={addTaskTargetEpisode}
        episodes={episodes}
        visitTypeOptions={visitTypeOptions}
        getClinicAssignableClinicians={getClinicAssignableClinicians}
        getClinicPhysicians={getClinicPhysicians}
        formatDuplicateClinicianOptionLabel={
          formatDuplicateClinicianOptionLabel
        }
        episodeRequiresAuthorization={episodeRequiresAuthorization}
        effectiveSelectedEpisodeId={effectiveSelectedEpisodeId}
        tasksRefreshVersion={tasksRefreshVersion}
        canViewSchedulingPointLoads={canViewSchedulingPointLoads}
        patientClinicId={patientClinicId}
        onCreated={() => fetchTasks()}
        onCommunicationCreated={communication =>
          setCommunications(prev => [...prev, communication])
        }
        invalidateSchedulingPointLoads={() => {
          void invalidateSchedulingPointLoads();
        }}
        showSuccess={showSuccess}
        showError={showError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTask}
        onOpenChange={open => !open && setDeleteTask(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            Are you sure you want to delete this task? This action cannot be
            undone.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteTask(null)}
              disabled={isDeletingTask}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteTask}
              disabled={isDeletingTask}
            >
              {isDeletingTask ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Episode Dialog */}
      <Dialog open={isEpisodeEditorOpen} onOpenChange={setIsEpisodeEditorOpen}>
        <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Edit Episode</DialogTitle>
            <DialogDescription className='sr-only'>
              Edit episode information.
            </DialogDescription>
          </DialogHeader>
          {selectedEpisode && canEditSelectedEpisode && (
            <EpisodeEditor
              episodeId={selectedEpisode.id}
              // Never undefined: the editor treats a missing prop as full access.
              capabilities={lockEpisodeCareFields(
                selectedEpisodeEditorCapabilities ?? REOPEN_ONLY_CAPABILITIES,
                chartLocked
              )}
              canReopen={canReopenEpisodes && !chartLocked}
              onCancel={() => setIsEpisodeEditorOpen(false)}
              onSaved={() => {
                setIsEpisodeEditorOpen(false);
                // Reopen/complete flips the closed lock on every task row.
                void handleVisitUpdated();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {episodeDateSync && (
        <EpisodeDateSyncDialog
          open
          boundary={episodeDateSync.boundary}
          episodeId={episodeDateSync.episodeId}
          visitId={episodeDateSync.visitId}
          newDate={episodeDateSync.newDate}
          currentBoundaryDate={episodeDateSync.currentBoundaryDate}
          episodeStartDate={episodeDateSync.episodeStartDate}
          episodeEndDate={episodeDateSync.episodeEndDate}
          visitLabel={episodeDateSync.visitLabel}
          onClose={() => setEpisodeDateSync(null)}
          onCompleted={() => {
            void fetchTasks({ silent: true });
            void loadEpisodes();
          }}
          onUpdateVisitOnly={() => {
            // Move only the visit, leaving the episode dates untouched. The
            // standard reschedule path enforces that the date stays inside the
            // episode window (the dialog only offers this when it does).
            const { visitId, newDate } = episodeDateSync;
            setEpisodeDateSync(null);
            void handleTaskUpdate(
              visitId,
              { scheduled_date: newDate },
              'Target date updated successfully.'
            );
          }}
        />
      )}

      <EpisodeInsuranceSelector
        open={isEpisodeInsuranceEditorOpen}
        onClose={() => setIsEpisodeInsuranceEditorOpen(false)}
        onConfirm={handleUpdateEpisodeInsurance}
        patientInsurances={patientInsurances}
        currentInsuranceId={selectedEpisode?.patient_insurance_id}
        insurancesLoading={insurancesLoading}
        patientId={id}
        episodeId={effectiveSelectedEpisodeId ?? undefined}
      />

      {/* Page level: the External EMR bar's Take over shows with a task panel
        too, and the list view is not mounted then. */}
      {takeoverCandidate && takeOverV2 && canTakeOverExternalEmrPeriod && (
        <TakeOverDialog
          open={takeOverOpen}
          onClose={() => setTakeOverOpen(false)}
          episodeId={takeoverCandidate.episode_id}
          onTakenOver={episode => {
            setEpisodes(prev =>
              prev.map(e => (e.id === episode.id ? episode : e))
            );
            setSelectedEpisodeId(episode.id);
            void loadEpisodes();
            void handleVisitUpdated();
            void refreshPatient();
          }}
        />
      )}

      {/* Page level, like BatchNotesDialog below, so the notice's Undo opens
        it with a task panel showing too. */}
      {undoTakeOverEpisodeId && (
        <UndoTakeOverDialog
          open
          onClose={() => setUndoTakeOverEpisodeId(null)}
          episodeId={undoTakeOverEpisodeId}
          onUndone={episode => {
            // Custody is derived: re-read the chart, its periods and its
            // tasks, so the notice goes and the External pills return.
            setEpisodes(prev =>
              prev.map(e => (e.id === episode.id ? episode : e))
            );
            void refreshPatient();
            void handleVisitUpdated();
          }}
        />
      )}

      {/* Mounted here, outside the tab and task-view conditionals, so a tab
        change while it is open cannot unmount it and strand its scroll lock. */}
      <BatchNotesDialog
        open={batchNotesOpen}
        onOpenChange={setBatchNotesOpen}
        tasks={batchNotesTasks}
        onSuccess={count => {
          showSuccess(
            'Note added',
            `Note added to ${count} task${count === 1 ? '' : 's'}.`
          );
          clearTaskSelection();
          void fetchTasks({ silent: true });
        }}
      />

      {/* EMR-325: EVV readings for the row whose indicator was clicked.
        Mounted only while open so each visit's readings are fetched fresh,
        matching how Hotbox drives this dialog. */}
      {evvVisitId && (
        <EvvReadingsDialog
          open={evvVisitId !== null}
          onOpenChange={open => {
            if (!open) setEvvVisitId(null);
          }}
          visitId={evvVisitId}
          canRemove={canRemoveEvv}
          onRemoved={() => void fetchTasks({ silent: true })}
        />
      )}

      {/* Episode Worksheet Editor */}
      {isEpisodeWorksheetOpen &&
        selectedEpisode &&
        canClaimSelectedEpisodeWorksheet && (
          <EpisodeWorksheetEditor
            episodeId={selectedEpisode.id}
            onClose={() => setIsEpisodeWorksheetOpen(false)}
          />
        )}
    </div>
  );
};

export default PatientDetail;
